// The Practice drill. Runs on SENSE CARDS (main.js drillCardsFrom): each card asks ONE meaning
// of a word — the cue is that sense's own POS + translations, never a sibling meaning's. Per-card
// counters post to api/exercise-session-sense and every interaction appends a user_answer_event
// row; a legacy card without a sense (pair data without rich senses) drills session-only and
// reports history at term grain. Every attempt ALSO posts an M5 presentation_event row at TERM
// grain (api/srs/presentation-event) — the observation layer of the M5 scheduler (m5-design §4).

// Client build stamp on every M5 presentation event — the capture-health monitors group by it,
// so a client rollout that breaks a channel is attributable. Bump on any capture-logic change.
var PL_CLIENT_BUILD = "20260724a";

// Coarse device class for the M5 motor-model strata (m5-design §4.3): typing speed differs ~2×
// across these. Pointer coarseness + viewport width — a heuristic, but a STABLE one per device,
// which is what a stratum key needs. Computed once per page load.
var plDeviceClass = (function () {
	try {
		var coarse = window.matchMedia && matchMedia("(pointer: coarse)").matches;
		if (!coarse) return "DESKTOP";
		var dim = Math.min(screen.width || 0, screen.height || 0);
		return dim >= 600 ? "TABLET" : "MOBILE";
	} catch (e) {
		return "OTHER";
	}
})();

// ── §4.3 allowance floor at L* (m5-design; PR 2.2 activation precondition) ───────────────────
// The live timer is gameplay, but it must never drop BELOW the canonical limit L* for the
// word's stratum: a timeout under a tightened timer is censored below the yardstick
// (censored_short — void for fitting), and adaptive-speed users would censor exactly their
// weakest memories. The server supplies the fitted per-(device, direction) motor constants;
// the floor is L* = α + β·K + γ·cueLen + τ in ms, computed with the SAME target string and
// longest-folding K the label derivation uses, so allotted_ms ≥ L*(longest) by construction.
var plMotorParams = null;   // {population: row, strata: [row]} from api/srs/motor-params

function plFetchMotorParams() {
	$.getJSON("api/srs/motor-params", function (data) {
		if (data && data.population) {
			plMotorParams = data;
		}
	}).fail(function () { /* no floor until fetched — capture must never break the drill */ });
}
$(plFetchMotorParams);   // on ready, not at parse: main.html holds ready until the page is shown

// TWIN of SrsLabelRules.keystrokeCost / umlautSubst / keystrokesLongest (Java) — the lockstep
// rule: a change to the keystroke-cost table or folding variants lands on BOTH sides in the
// same PR, or the floor stops matching the yardstick the labels are scored against.
function plKeystrokeCost(s) {
	var sum = 0;
	for (var i = 0; i < s.length; i++) {
		var c = s.charAt(i);
		var upper = c !== c.toLowerCase() && c === c.toUpperCase();
		sum += 1 + (upper || "äöüÄÖÜß".indexOf(c) >= 0 ? 1 : 0);
	}
	return sum;
}

function plUmlautSubst(s) {
	return s.replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue")
		.replace(/Ä/g, "Ae").replace(/Ö/g, "Oe").replace(/Ü/g, "Ue").replace(/ß/g, "ss");
}

// K for the floor: the reading most generous to the user (§4.3 "longest accepted form").
function plKeystrokesLongest(s) {
	return Math.max(plKeystrokeCost(s), plKeystrokeCost(plUmlautSubst(s)));
}

// The stratum's constants, falling back to the population row until the stratum warms up.
function plMotorRow(deviceClass, direction) {
	if (!plMotorParams) return null;
	var strata = plMotorParams.strata || [];
	for (var i = 0; i < strata.length; i++) {
		if (strata[i].deviceClass === deviceClass && strata[i].direction === direction) {
			return strata[i];
		}
	}
	return plMotorParams.population || null;
}

// L* in ms for one showing — 0 when the constants have not arrived yet (no floor; the
// censored_short monitor watches the residual rate).
function plLStarFloorMs(target, cueLen, deviceClass, direction) {
	if (!target) return 0;
	var row = plMotorRow(deviceClass, direction);
	if (!row) return 0;
	return Math.round(row.alphaMs + row.betaMsPerKey * plKeystrokesLongest(target)
		+ (row.gammaMsPerCueChar || 0) * (cueLen || 0)) + row.tauMs;
}

function TrainingSession(cardList, sessionMeta) {
	var defExerciseField = $("#defExerciseId");
	var lookupField = $("#lookupFieldId");
	var answerField = $("#answerId");
	var answerPrefixField = $("#answerPrefixId");
	var revealWordBtn = $("#revealWordId");
	var tipBox = $("#tipId");

	// The subject pronoun shown as static text beside the answer field on the current card, or ""
	// when the card isn't a prefilled conjugated form. Set once per card by applyAnswerPrefix and
	// re-attached by getAnswerText, so nothing downstream — matching, the wrong-answer diff, the
	// answer-event log — ever sees a half answer.
	var answerPrefix = "";

	// The "Reveal the word" button surfaces only after two genuine wrong attempts on the current
	// word (see processUserAnswer); toggled by a class so jQuery .show() can't clobber its flex.
	// It shares the field's right end with the typing mapping and keeps the corner, so its width
	// (measured while it is actually showing — it has none before that) is what the mapping and the
	// field's padding stand clear of. Both toggles re-ask whether the typed text now reaches the
	// mapping, since the room just changed under it.
	var answerBox = $("#answerBoxId");
	var answerSubst = $("#answerSubstId");
	var substFit = TypeSubst.watch(answerField, answerBox, answerSubst);

	function setRevealWidth(px) {
		answerBox[0].style.setProperty("--pl-reveal-w", px + "px");
		TypeSubst.remeasure(answerBox);
		substFit();
	}
	function showRevealWord() {
		revealWordBtn.addClass("revealShownClass");
		setRevealWidth(Math.ceil(revealWordBtn.outerWidth()) + 10);
	}
	function hideRevealWord() {
		revealWordBtn.removeClass("revealShownClass");
		setRevealWidth(0);
	}

	// The language THIS CARD is answered in — langTo normally, langFrom when the card is reversed,
	// which in a Mix session changes card by card. The same read the answer placeholder makes, and
	// what decides whether the field carries a typing mapping (ä→ae …) and which one.
	function typedLangCode(td) {
		return ($(td && td.reverse ? "#langFromValId" : "#langToValId")
			.attr("data-lang-code") || "").toUpperCase();
	}

	// Repaint the in-field typing mapping for the card in hand — nothing at all in a language with
	// no substitutions to name, which is every language but German today. Passing no card clears
	// it, which is what leaving the drill wants.
	function applyTypeSubst(td) {
		TypeSubst.apply(answerBox, answerSubst, answerField, td ? typedLangCode(td) : "", TypeSubst.ANSWER);
	}

	// ── the forward action on a revealed answer ──────────────────────────────────
	// Moving on has always been Enter, and Enter's only prompt was the answer field's placeholder —
	// which the browser hides the moment the field holds text, and after a wrong answer the field
	// still holds the wrong answer (kept on purpose, so the learner can compare it with the diff).
	// So the one screen that most needs a visible way forward was the one screen with none.
	//
	// The button is the Learn ladder's, deliberately: same pill classes, same label resource, same
	// top-right corner of the centre pane (learning_session.js setTopAction). Advancing is one
	// gesture across the app, not a per-tab invention. It goes through processUserAnswer rather
	// than straight to the next card so the click inherits everything Enter has there — the
	// just-missed debounce above all.
	var session = this;
	var continueHost = $("#exerciseContinueId");
	var exerciseBox = $("#defExerciseBoxId");

	function showContinueAction() {
		var btn = $("<button>").attr("type", "button")
			.addClass("lrnCtaButtonClass lrnTopActionBtnClass")
			.text(getRes("learnMiss_continue"))
			.click(function () { session.processUserAnswer(); });
		continueHost.empty().append(btn);
		exerciseBox.addClass("exerciseContinueShownClass");   // opens room for it in the head row
	}

	function hideContinueAction() {
		continueHost.empty();
		exerciseBox.removeClass("exerciseContinueShownClass");
	}

	var toShow = [];//List of TrainingDef objects (the objects are being deleted from list)
	var allDefs = [];//ALL TrainingDefs, kept whole — toShow splices mastered cards out per cycle,
	                 //but the finish summary needs every card's counters
	var initialWordCount = 0;//set size at start — the overall-progress bar's denominator
	var currIndex = 0;
	var defReturned = false;
	var lastWrongAnswerTs;
	var sessionStartTs = null;//wall-clock start, for the finish summary's session time

	var trainingDef;

	var isSessionStopped = false;

	var sessionId = null;
	// This session's own id, made here and sent with the start and with every record after it
	// (docs/project/restart-notice-and-resend.md): a record no longer has to wait for the server's
	// number (sessionId) to name its session, and a start sent twice finds the session it made.
	var sessionKey = PlConn.newId();
	// The newest session on this page. A session end can now be answered LATE (held through a
	// restart or a dropped connection); by then the user may have started another drill, whose
	// screen this reply must not paint into.
	TrainingSession.latestKey = sessionKey;
	sessionMeta = sessionMeta || {};

	// Reverse direction (DE→EN): the cue is the German word, the user types an English
	// translation. PER CARD — `card.reverse`, stamped by main.js at deck build (drillDeckFrom /
	// assignMixDirections), because a Mix session drills half its words each way. Everything that
	// behaves differently by direction reads the card in hand; these two session-level flags exist
	// only for what is genuinely session-grained (the session row's direction). The mode is
	// decided ONCE at session start — a mid-session toggle only affects the next session.
	var sessionReverse = !!sessionMeta.reverse;
	var sessionMixed = !!sessionMeta.mixed;

	// Every drill record goes through PlConn (connection.js): kept and sent again until the server
	// has it, in order, with a session end (barrier) going only after everything before it.
	function postStats(url, params, onSuccess, barrier) {
		return PlConn.post(url, params, { onSuccess: onSuccess, barrier: !!barrier });
	}

	// In-flight per-card posts — progress (api/exercise-session-sense) AND answer events
	// (api/answer-event). The session-end call MUST NOT overtake either: at end time the server
	// derives this session's labels from the answer events and then runs the garden advance and the
	// §9.1 outcome mapper off those and the progress rows. The last card's posts race the end by
	// microseconds — when the end won, the word's watering and its finish-screen context were
	// silently lost.
	//
	// Since the state⇔flower invariant (2026-08-03) the answer events belong in this wait too, and
	// a session the user STOPS waits exactly like one that finishes. An end that overtook the last
	// answer event would derive a label saying the word was never retired — so no flower — and then
	// the 60-second re-derivation would see the late event, flip the same label to retired, and let
	// the engine mint state for a word with no plant: precisely the state the invariant forbids.
	var inFlightCardPosts = [];
	function whenCardPostsSettled(done) {
		var settled = inFlightCardPosts.map(function (x) {
			return $.Deferred(function (d) { x.always(d.resolve); });
		});
		inFlightCardPosts = [];
		// A hung post must not hold the finish data hostage: fire after 4 s regardless (the
		// server's monotone upsert makes a late-landing post harmless), but only once.
		var fired = false;
		var fire = function () {
			if (!fired) { fired = true; done(); }
		};
		setTimeout(fire, 4000);
		$.when.apply($, settled).always(fire);
	}

	// Set when the user answers the empty field with Enter (asking to see the answer): the timer
	// jump that follows is a REVEAL in the answer history, not a TIMEOUT. Reset where consumed.
	var revealRequested = false;

	// Client-side per-session event ordinal (0042 `seq`): events are fire-and-forget ajax, so the
	// server's receive order is unreliable — this is the true interaction sequence. All lockstep
	// sense rows of one interaction share one seq.
	var eventSeq = 0;

	// The drilled direction of ONE card, for the answer history. Precedence: what the user TYPES
	// decides (design/practice-direction-controls.md §5). A reverse card asks for the meaning
	// whether its cue was text or audio → TERM_TO_MEANING, with the audio recorded separately as
	// sound_only_cue; only an audio cue answered with the WORD is DICTATION (hear it, type it — a
	// distinct retrieval task, 0042). Audio+reverse used to collapse into DICTATION, which pooled
	// two different tasks into one motor stratum.
	function currentDirection(td) {
		if (td && td.reverse) return 'TERM_TO_MEANING';
		if (sessionAudioCue) return 'DICTATION';
		return 'MEANING_TO_TERM';
	}

	// ── M5 presentation events (m5-design §4.1/§4.4) ─────────────────────────────
	// One api/srs/presentation-event JSON post per ATTEMPT (changed-text commit, or the attempt a
	// timeout/reveal ended), at TERM grain. Events can fire before the session-start response
	// returns the server session id; they used to queue here until it arrived, and now carry the
	// session key instead (a NULL session would orphan the probe row from its session).

	function postPresentationEvent(td, o) {
		if (!td) return;
		var termId = td.getTerm() && td.getTerm().id;
		if (!termId || !td.presentationId) return;
		var now = new Date();
		// "The cue was audio" — not the same thing as the DICTATION direction any more: an
		// audio-cued reverse card is TERM_TO_MEANING with sound_only_cue true. What this flag
		// still governs is the cue itself: no cue text, and the retrieval clock anchored on the
		// audio's end.
		var isDictation = sessionAudioCue;
		// Dictation's retrieval clock starts when the (first) cue audio FINISHES (§4.1): a
		// latency measured from cue-shown would count the audio's own duration as thinking.
		var firstKeyMs = td.firstKeyMs;
		if (isDictation) {
			firstKeyMs = (td.firstKeyAbsTs != null && td.audioEndTs != null)
				? Math.max(0, td.firstKeyAbsTs - td.audioEndTs) : null;
		}
		var cue = isDictation ? null : td.getCueText();
		var payload = {
			termId: termId,
			userSessionId: sessionId,
			presentationId: td.presentationId,
			attempt: td.wrongEnterCountThisPresentation + 1,
			seq: ++eventSeq,
			shownTs: td.shownAtTs,
			direction: currentDirection(td),
			deviceClass: plDeviceClass,
			// BUTTON says no text entered the field at all — the learner self-graded (§6.2). It is
			// what the server reads to flag the label self_graded and apply α_self, and it also
			// keeps these rows out of the motor/τ calibration, which selects on KEYS.
			inputMode: td.selfCheck ? 'BUTTON'
				: (td.tlPasted ? 'PASTE' : (td.tlComposed ? 'IME' : 'KEYS')),
			requiredTarget: (formsFor(td) || [])[0] || null,
			submittedText: o.submitted != null && o.submitted !== "" ? o.submitted : null,
			correct: !!o.correct,
			similarity: o.similarity != null ? o.similarity : null,
			firstKeyMs: firstKeyMs,
			completionMs: o.completionMs != null ? o.completionMs : null,
			keystrokes: td.keyCount || 0,
			backspaces: td.backspaceCount || 0,
			longestPauseMs: td.longestPauseMs || 0,
			editTimeline: td.tlEntries && td.tlEntries.length ? td.tlEntries : null,
			allottedMs: td.allottedMs,
			replaysBefore: td.replaysBefore || 0,
			cueLen: cue ? cue.length : 0,
			repetition: getRepetition(),
			strictCaps: isStrictCaps(),
			drillArticle: isDrillArticle(),
			adaptiveSpeed: isAdaptiveSpeed(),
			soundOnlyCue: isDictation,
			autoSoundPlayed: !!td.autoSoundPlayed,
			termMode: !!(window.isTermUnitMode && window.isTermUnitMode()),
			localHour: now.getHours(),
			localDow: now.getDay(),
			clientBuild: PL_CLIENT_BUILD,
			cueShown: cue
		};
		sendPresentationEvent(payload);
		// The attempt is over — the next edits belong to the NEXT attempt's timeline segment.
		td.tlEntries = [];
		td.tlDiverged = false;
		td.tlInBackspaceRun = false;
		td.tlPasted = false;
		td.tlComposed = false;
	}

	// Sent at once, never held back for the session number any more: the session key on the query
	// names the session when userSessionId is still null (the first word can be answered before the
	// start's reply), and PlConn keeps the order — this event before the session end.
	function sendPresentationEvent(payload) {
		PlConn.post("api/srs/presentation-event?sessionKey=" + encodeURIComponent(sessionKey),
			payload, { json: true });
	}

	// One edit-timeline entry (§4.4): t = ms since the cue was shown, k = b|d|c, p = position.
	function tlPush(td, kind, pos) {
		if (!td || !td.shownAtTs || !td.tlEntries) return;
		if (td.tlEntries.length >= 120) return;   // summary, never a stream
		td.tlEntries.push({ t: Date.now() - td.shownAtTs, k: kind, p: pos });
	}

	// First divergence from the target (§4.4): the moment the field stops being a prefix of ANY
	// accepted form (case-insensitive — this is a timing signal, not the grading pass). p = the
	// length of the longest still-matching prefix, i.e. where the text went wrong.
	function tlCheckDivergence(td) {
		if (!td || td.tlDiverged || !td.tlEntries) return;
		var typed = doc.getAnswerText().replace(/\s+/g, " ");
		if (!typed) return;
		var upper = typed.toUpperCase();
		var forms = formsFor(td) || [];
		var best = 0;
		for (var i = 0; i < forms.length; i++) {
			var form = forms[i].toUpperCase();
			if (form.indexOf(upper) === 0) return;   // still a clean prefix — no divergence
			var n = Math.min(upper.length, form.length), k = 0;
			while (k < n && upper.charAt(k) === form.charAt(k)) k++;
			if (k > best) best = k;
		}
		td.tlDiverged = true;
		tlPush(td, 'd', best);
	}

	// One append-only user_answer_event row per interaction — fire-and-forget telemetry, never
	// blocking the drill. Cards identify by sense id; a legacy sense-less card reports at term
	// grain. Sound-only cues carry no cue_shown text. Every event also carries the 0042 SRS
	// instrumentation: presentation grouping, first-key latency + typing summary, the timer
	// allowance, pre-answer replay count and the ⚙ settings flags (read from the DOM per event — a
	// session snapshot goes stale on a mid-session toggle; the cue mode is the exception, fixed at
	// session start along with the direction).
	function postAnswerEvent(td, outcome, extras) {
		if (!td) return;
		var now = new Date();
		var params = {
			mode: 'PRACTICE',
			direction: currentDirection(td),
			outcome: outcome,
			seq: ++eventSeq,
			strictCaps: isStrictCaps(),
			drillArticle: isDrillArticle(),
			adaptiveSpeed: isAdaptiveSpeed(),
			soundOnlyCue: sessionAudioCue,
			termMode: !!(window.isTermUnitMode && window.isTermUnitMode()),
			localHour: now.getHours(),
			localDow: now.getDay()
		};
		if (td.presentationId) params.presentationId = td.presentationId;
		// 1-based within the presentation, counting CHANGED attempts only (same-text re-Enters
		// are deduped before this runs): a FAIL posts before its own increment, so +1 numbers it;
		// a TIMEOUT/REVEAL is the attempt still in progress.
		params.attempt = td.wrongEnterCountThisPresentation + 1;
		if (td.firstKeyMs != null) params.firstKeyMs = td.firstKeyMs;
		if (td.keyCount) params.keystrokes = td.keyCount;
		if (td.backspaceCount) params.backspaces = td.backspaceCount;
		if (td.longestPauseMs) params.longestPauseMs = td.longestPauseMs;
		if (td.allottedMs != null) params.allottedMs = td.allottedMs;
		params.replaysBefore = td.replaysBefore || 0;
		params.autoSoundPlayed = !!td.autoSoundPlayed;
		if (!sessionAudioCue) {
			var cue = td.getCueText();
			if (cue) params.cueShown = cue;
		}
		if (sessionId) params.userSessionId = sessionId;
		params.sessionKey = sessionKey;   // names the session when its number is not back yet
		if (td.shownAtTs) params.latencyMs = Date.now() - td.shownAtTs;
		if (extras) {
			for (var k in extras) {
				if (extras[k] != null) params[k] = extras[k];
			}
		}
		// One request per interaction: in term mode the whole word's senses record the same
		// interaction (lockstep), and the server writes one row per sense in one transaction. A legacy
		// sense-less card reports at term grain instead.
		var senseIds = td.getSenseIds();
		if (senseIds.length) {
			params.senseIds = senseIds;
			inFlightCardPosts.push(postStats("api/answer-event", params, null));
		} else {
			var termId = td.getTerm() && td.getTerm().id;
			if (!termId) return;
			params.termId = termId;
			if (sessionMeta.langFrom) params.langFrom = sessionMeta.langFrom;
			inFlightCardPosts.push(postStats("api/answer-event", params, null));
		}
	}

	var delayedSoundId;
	// The dictation cue's auto-repeat state (see startCueLoop). Timers are collected rather than
	// held in one slot because the loop has two pending at once — the next playback and its
	// 'ended' backstop — and cueGen makes any survivor of a clear() inert on arrival.
	var cueGen = 0;
	var cueTimers = [];
	// Whether THIS card's cue loop got as far as starting the allowance. Read by resumeSession: a
	// pause landing while the cue is still speaking has no bar to resume.
	var cueStartedBar = false;

	var doc = new Doc();

	// "Cue by ear" for THIS session: the cue is the term's audio instead of its text. Captured once,
	// from main.js's isAudioCue (the speaker toggle in the Start band's direction cluster, backed by
	// the defSoundOnly attr) — the cluster is locked while a session runs, so like the direction
	// this is fixed at session start and every read below is of one settled value, not of the DOM.
	var sessionAudioCue = !!(window.isAudioCue && window.isAudioCue());
	var correctAnswerShown = false;//user's answer was wrong, correct answer is being shown

	var progressBar = new ProgressBar(function () {
		answerWasWrong();
	});
	progressBar.setToZero();

	{//Initialize:
		doc.setDefExerciseText("");

		// Warm every card's sound (url + bytes) up front — the sound-only rung otherwise pays a
		// fresh fetch on every card, including its own auto-repeats (see startCueLoop).
		// The full batch, uncapped/unstaggered: a training session's card count is bounded by its
		// own settings, nowhere near PlSound's viewport-prefetch cap for an open-ended word list.
		var prefetchItems = [];
		for (var p = 0; p < cardList.length; p++) {
			var pc = cardList[p].cluster || cardList[p];
			if (pc && pc.term && pc.term.id != null) {
				prefetchItems.push({ termId: pc.term.id, variantId: pc.ipaVariantId });
			}
		}
		window.PlSound.prefetchMany(prefetchItems);

		for (var i = 0; i < cardList.length; i++) {
			toShow.push(new TrainingDef(cardList[i]));
		}
		allDefs = toShow.slice();

		makeSoundOnly(sessionAudioCue);

		function flushBeacon() {
			if (!trainingDef || !defReturned) return;
			var senseIds = trainingDef.getSenseIds();
			if (!senseIds.length) return;   // legacy sense-less card — drills session-only
			var params = {
				sessionKey: sessionKey,
				senseIds: senseIds,
				timesPresented: trainingDef.presentedCount,
				timesCorrect: trainingDef.correctCount,
				timesTimedOut: trainingDef.timeoutCount,
				wrongEnterAttempts: trainingDef.wrongEnterCount,
				soundReplays: trainingDef.soundReplayCount,
				firstTryCorrect: trainingDef.firstTryCorrect,
				mastered: trainingDef.mastered
			};
			if (trainingDef.totalResponseMs > 0) params.totalResponseMs = trainingDef.totalResponseMs;
			if (sessionId) params.sessionId = sessionId;
			// One beacon for the whole word — the server advances every sense in lockstep (term mode).
			// Traditional $.param: senseIds=1&senseIds=2, the repeated-key form the server binds.
			// ⛔ ROOT-relative: sendBeacon bypasses util.js's ajaxPrefilter, so a bare "api/…" resolved
			// against the page's directory — /de/en/api/… (404) on the guest app page every ad visitor
			// lands on — and the beacon was silently lost there until 2026-10-06.
			navigator.sendBeacon('/api/exercise-session-sense',
				new Blob([$.param(params, true)], { type: 'application/x-www-form-urlencoded' }));
		}
		window.addEventListener('pagehide', flushBeacon);
		document.addEventListener('visibilitychange', function () {
			if (document.visibilityState === 'hidden') flushBeacon();
		});

		// "Reveal the word": clicking it asks to see the answer — the same path as pressing Enter on an
		// empty field (logged as a REVEAL, not a timeout). Only meaningful while a question is live.
		revealWordBtn.text(getRes("revealWord_label")).assignTooltip("revealWord_tooltip");
		hideRevealWord();
		revealWordBtn.off("click").on("click", function () {
			if (isSessionStopped || !defReturned || !trainingDef) { return; }
			revealRequested = true;
			hideRevealWord();
			progressBar.moveToEnd();   // → answerWasWrong reveals the answer
		});

		var pane = $("#paneId");
		pane.unbind("keydown");
		// Space replays the cue — the app-wide sound key (Dictionary and Learn use the same one).
		// A CARET BEATS THE SHORTCUT, ALWAYS: while the focus sits in a text field Space is a space,
		// full stop (owner report 2026-08-10 — a revealed phrase answer could not be retyped, because
		// every space between its words played the sound instead). That is the same rule main.js's
		// spaceIsIdle and the Learn ladder already follow; the drill was the one holdout, and its
		// answer field holds focus for the whole card, so in practice the key is now the 🔊 button —
		// which is why every state that may be heard paints one (cueSoundButton below).
		// Space is still the key on the reverse SELF-CHECK card, whose focus is the pane itself.
		// (A Ctrl+Shift+Space chord used to cover the typing case app-wide; dropped 2026-07-27.)
		pane.keydown(function (e) {
			if (e.keyCode !== PL.keys.SPACE || e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) { return; }
			// The same claims spaceIsIdle (main.js) recognises: a caret owns Space, and so does a
			// focused button — including the replay speaker itself, which would otherwise play twice.
			var t = e.target, tag = (t && t.tagName || "").toLowerCase();
			if (tag === "input" || tag === "textarea" || tag === "button" || tag === "select") { return; }
			if (t && (t.isContentEditable || (t.getAttribute && t.getAttribute("role") === "button"))) { return; }
			if (!trainingDef || !(sessionAudioCue || trainingDef.reverse || correctAnswerShown)) { return; }
			// Holding the key must not machine-gun the audio — and must not inflate replaysBefore,
			// which is a graded SRS signal (0042).
			if (e.originalEvent && e.originalEvent.repeat) { e.preventDefault(); return; }
			if (correctAnswerShown) {
				trainingDef.soundReplayCount++;
			} else {
				// Pre-reveal replay = the user ASKING to hear the cue again before answering —
				// the 0042 replays_before signal (mapper: no Easy grade on an aided recall).
				trainingDef.replaysBefore = (trainingDef.replaysBefore || 0) + 1;
			}
			replayCue();
			e.preventDefault();
			e.stopPropagation();
		});

		// Self-check keyboard (§6.1): the digit shown on a button presses it, and Enter takes the
		// first option — so a pass is two keypresses ("1", "1") and the hands never leave the
		// keyboard. Same idiom as the Learn ladder's numbered options, deliberately. Bound on the
		// pane rather than the document because the drill's pane already owns Space, and a
		// self-check card focuses the pane exactly so these have somewhere to land.
		pane.keydown(function (e) {
			if (!trainingDef || !trainingDef.selfCheck || correctAnswerShown || isSessionStopped) { return; }
			if (e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) { return; }
			var btns = selfCheckBox.find("._selfBtnClass");
			if (!btns.length) { return; }
			var n = (e.key >= "1" && e.key <= "9") ? parseInt(e.key, 10)
				: (e.keyCode >= 49 && e.keyCode <= 57) ? e.keyCode - 48
				: (e.keyCode === PL.keys.ENTER) ? 1 : 0;
			if (n >= 1 && n <= btns.length) {
				e.preventDefault();
				e.stopPropagation();
				btns.eq(n - 1).click();
			}
		});

		// 0042 typing instrumentation on the answer field: first_key_ms = cue shown → FIRST
		// keydown (the retrieval-latency signal, reset per presentation in setNextDefinition);
		// keystrokes/backspaces/longest inter-key pause are the deliberate summary stats (no full
		// key log). Counted only while a question is live; pure modifier presses don't count.
		// The M5 edit timeline (§4.4) rides on the same handler: one 'b' entry per backspace RUN
		// (position = where the run started deleting), never one per keypress.
		answerField.off("keydown.plSrs").on("keydown.plSrs", function (e) {
			var td = trainingDef;
			if (!td || !defReturned || correctAnswerShown || isSessionStopped) return;
			var kc = e.which || e.keyCode;
			if (kc === 16 || kc === 17 || kc === 18 || kc === 20 || kc === 91 || kc === 92) return;
			var now = Date.now();
			if (td.firstKeyMs == null && td.shownAtTs) td.firstKeyMs = now - td.shownAtTs;
			if (td.firstKeyAbsTs == null) td.firstKeyAbsTs = now;   // dictation clocks from audio end
			td.keyCount = (td.keyCount || 0) + 1;
			if (kc === 8 || kc === 46) {
				td.backspaceCount = (td.backspaceCount || 0) + 1;
				if (!td.tlInBackspaceRun) {
					td.tlInBackspaceRun = true;
					var pos = null;
					try { pos = e.target.selectionStart; } catch (ignored) {}
					tlPush(td, 'b', pos != null ? pos : (doc.getAnswerText() || "").length);
				}
			} else if (kc !== 13) {
				td.tlInBackspaceRun = false;
			}
			if (td.lastKeyTs) {
				var gap = now - td.lastKeyTs;
				if (gap > (td.longestPauseMs || 0)) td.longestPauseMs = gap;
			}
			td.lastKeyTs = now;
		});

		// M5 input-mode + divergence detection (§4.3/§4.4), from input-EVENT types — never from
		// keystroke counts (a count heuristic would void every swipe-keyboard user): a paste marks
		// the attempt PASTE (input anomaly, label voided server-side later), IME composition marks
		// it IME (measured per-character, not voided). Each 'input' also checks whether the field
		// just stopped being a prefix of any accepted form — the first-divergence timeline entry.
		answerField.off("paste.plSrs").on("paste.plSrs", function () {
			if (trainingDef) trainingDef.tlPasted = true;
		});
		answerField.off("compositionstart.plSrs").on("compositionstart.plSrs", function () {
			if (trainingDef) trainingDef.tlComposed = true;
		});
		answerField.off("input.plSrs").on("input.plSrs", function () {
			var td = trainingDef;
			if (!td || !defReturned || correctAnswerShown || isSessionStopped) return;
			updateGapEcho();
			tlCheckDivergence(td);
		});
	}

	// Live echo (design §5): what is typed appears in the blank as it is typed. The answer FIELD
	// stays the real input — it carries first-key timing, backspace counting, paste/IME detection
	// and the edit timeline the SRS engine consumes, and a genuinely inline-editable gap would mean
	// rebinding every one of them. This is a read-only mirror; nothing underneath changes.
	function updateGapEcho() {
		var plan = trainingDef && trainingDef.getGapPlan();
		if (!plan || trainingDef.gapRevealed) return;
		var spans = defExerciseField.find("._gapClass");
		if (!spans.length) return;
		var filled = PhraseGap.distribute(doc.getAnswerText(), plan.gaps.length);
		spans.each(function (i) {
			var text = filled[i] || "";
			$(this).text(text).toggleClass("_gapFilledClass", !!text);
		});
	}

	// The drill's replay speaker — ONE builder for every place the current card can be heard, because
	// Space no longer replays while the caret is in the answer field (see the pane handler) and a
	// button the eye can find is what replaced it. It wears the rich head's face (.ai-sound-btn:
	// borderless round, floats top-right, big glyph), the same one every rich entry shows, so a word
	// is heard the same way wherever it appears.
	//
	// The press is also a graded SRS signal: before the answer is out it is the 0042 replays_before
	// count (a re-hear that ASKED for help — the mapper allows no Easy grade on an aided recall);
	// after the reveal it is a plain soundReplayCount. Same accounting the Space key had.
	function cueSoundButton() {
		return $("<button>").attr("type", "button")
			.addClass("ai-sound-btn")
			.append($("<span>").addClass("pl-ic pl-ic-volume"))   // the app-wide volume icon, not a 🔊 emoji
			.assignTooltip("tsSound_tooltip")
			.click(function (e) {
				e.stopPropagation();
				if (!trainingDef) { return; }
				if (correctAnswerShown) {
					trainingDef.soundReplayCount++;
				} else {
					trainingDef.replaysBefore = (trainingDef.replaysBefore || 0) + 1;
				}
				replayCue();
			});
	}

	// The practice microphone for a word already on screen — the drill's half of the app-wide
	// Mic · Sound pair (ai-features-plan.md §1 decision 1; no Add control exists in the drill).
	// Floats right like its neighbour, so PREPENDING it after the speaker reads as mic-then-speaker.
	//
	// ⚠️ It is deliberately never planted on a SOUND-ONLY cue. That cue is a prompt with the word
	// hidden, and this button opens a popup whose first line is the word — the same line the drill
	// draws for the Space key and for addCueSound: "revealing it before it has been asked for would
	// hand it over". Every place the word IS on screen gets one.
	function cueMicButton() {
		var td = trainingDef;
		var termDto = td && td.getDefCluster() && td.getDefCluster().term;
		if (!termDto || termDto.id == null) { return null; }
		var ipa = window.plPopupIpa
			? window.plPopupIpa(termDto.term, termDto.transcription) : "";
		return $("<button>").attr("type", "button")
			.addClass("pl-btn-toggle pronMicClass pronMicLgClass")
			.attr("aria-pressed", "false")
			.append($("<span>").addClass("pl-ic pl-ic-mic"))
			.assignTooltip("pronMic_tooltip")
			.on("click", function (e) {
				e.stopPropagation();
				PRONOUNCE.open(this, {
					termId: termDto.id, text: termDto.term, article: termDto.article, ipa: ipa,
					ipaVariantId: td.getDefCluster().ipaVariantId || null
				});
			});
	}

	// The speaker for a REVEALED answer. A full rich entry brings its own in the head, so this only
	// fills the two cases that have none: a phrase reveal (main.js shows the bare headword — no rich
	// lookup is ever run on a sentence) and a card whose word has no rich entry at all, where the
	// plain definition is all there is. Prefers the head so the icon sits beside the word it speaks.
	function addRevealSound() {
		var head = defExerciseField.find(".rich-head").first();
		if (head.length && head.find(".ai-sound-btn").length) { return; }
		var target = head.length ? head : defExerciseField;
		var mic = cueMicButton();
		if (mic) { target.prepend(mic); }
		target.prepend(cueSoundButton());
	}

	// Paint the cue area for the session's cue mode: heard (a replay button + "listen to the sound")
	// or read (the card's own cue text, at the word box's larger type).
	function makeSoundOnly(soundOnly) {
		var defText = "";
		if (soundOnly) {
			// The answer language, flag + native name, lives INSIDE the prompt sentence: the input
			// placeholder also names it, but that vanishes on the first keystroke — in Mix the
			// direction changes per card, so the pane must keep saying what to type. Per-card:
			// langTo normally, langFrom when THIS card is reversed (same rule as the placeholder).
			var langSel = $(trainingDef && trainingDef.reverse ? "#langFromValId" : "#langToValId");
			// No context argument on purpose: which of the two languages is flagged here flips with
			// the card's direction, so the resolver's own default — "learning" when the code IS the
			// learned language, "language" otherwise — is exactly the rule this prompt needs. On a
			// forward card the learner sees the English THEY chose; on a reversed one, where the
			// answer is in their own language, English (if that is it) shows as English.
			var flagHtml = window.plLangFlagHtml ? window.plLangFlagHtml(langSel.attr("data-lang-code")) : "";
			var langHtml = "<span class='soundOnlyLangClass'>" + flagHtml + " "
				+ getFirstText(langSel) + "</span>";
			// The replay speaker wears the rich head's face (.ai-sound-btn floats right, big icon) —
			// same button, same corner, wherever a word can be heard. (The 4 s auto-repeat is NOT
			// counted as a replay by the builder — only this button's presses are user-initiated.)
			defText = "" +
			"<span id='soundOnlyPlayId' class='_soundClass'></span>" +
			"<span class='subduedClass'>" +
			"    " + strFormat(getRes("tsListToSound"), langHtml) +
			"</span>";
			doc.setDefExerciseText(defText);
			$("#soundOnlyPlayId").append(cueSoundButton());
			defExerciseField.removeClass("largeFont");
		} else {
			if (trainingDef) {
				doc.setDefExerciseText(trainingDef.getDefinition());
			}
			defExerciseField.addClass("largeFont");
		}
	}

	// The read cue's own speaker, painted per card by setNextDefinition (makeSoundOnly runs once, at
	// session start, before there is a card to sound). A REVERSE card's cue IS the word being learned
	// — it even auto-pronounces on arrival — so the replay button belongs on it, exactly as in the
	// sound-only cue. A forward card gets none: its cue is the translation, and sounding the answer
	// before it has been asked for would hand it over. That is the line the Space key drew, and the
	// button draws the same one.
	function addCueSound() {
		if (!trainingDef || !trainingDef.reverse) { return; }
		// A reverse card's cue IS the learned word, printed on screen — so the microphone belongs
		// here too, and nothing is given away by it. (The sound-only cue, where the word is hidden,
		// is the one speaker in the app with no microphone beside it; see cueMicButton.)
		var mic = cueMicButton();
		if (mic) { defExerciseField.prepend(mic); }
		defExerciseField.prepend(cueSoundButton());
	}

	// Resolve/cache/prefetch/play is the shared PlSound module (static/js/sound.js) — see
	// prefetchBatchSounds below for the batch warm-up fired at session construction.
	function playSound(defCluster, onEnded) {
		var termId = defCluster.term && defCluster.term.id;
		if (!termId) return;
		// A sense card carries its heteronym pronunciation variant (0038) — the drill
		// speaks the drilled sense's stress, not the term default.
		var variantId = defCluster.ipaVariantId || null;
		window.PlSound.play(termId, variantId, onEnded);
	}

	// Under dictation the answer allowance starts when the cue STOPS SPEAKING, not when the card
	// appears — so whichever playback gets there first calls this: the automatic loop's first play,
	// its backstop, or a manual replay that superseded the loop before it managed it. Idempotent
	// per card (cueStartedBar is reset by startCueLoop), because all three can race.
	function beginAllowance(td, fromEnded) {
		if (cueStartedBar) { return; }
		cueStartedBar = true;
		// §4.1: dictation's retrieval clock starts when the FIRST cue playback ends — the repeats
		// and manual replays never move it. Only a REAL 'ended' dates audioEndTs; timing a playback
		// that never happened would be a fabricated number, and null already means "unknown"
		// downstream (firstKeyMs, line 277).
		if (fromEnded && td && td.audioEndTs == null) { td.audioEndTs = Date.now(); }
		progressBar.start(td);
	}

	// "Say it again", from bare Space or the cue's own speaker button.
	//
	// Ordinarily a plain replay — but pressed while the AUTOMATIC cue is still speaking it has to
	// take the loop's other job over too. PlSound.play() pauses the current clip, and pause() fires
	// no 'ended', so the loop's first playback would never settle and the allowance would wait for
	// the CUE_MAX_MS backstop. So the loop stands down and THIS playback starts the allowance.
	function replayCue() {
		if (!trainingDef) { return; }
		var defCluster = trainingDef.getDefCluster();
		if (!sessionAudioCue || cueStartedBar || correctAnswerShown) { playSound(defCluster); return; }
		var gen = ++cueGen;   // stand the automatic loop down — the learner has taken over
		for (var i = 0; i < cueTimers.length; i++) { clearTimeout(cueTimers[i]); }
		cueTimers = [];
		var td = trainingDef;
		playSound(defCluster, function () { if (gen === cueGen) { beginAllowance(td, true); } });
		cueTimers.push(setTimeout(function () {
			if (gen === cueGen) { beginAllowance(td, false); }
		}, CUE_MAX_MS));
	}

	// The dictation cue: spoken on arrival, then up to CUE_MAX_PLAYS times in all, with
	// CUE_GAP_MS of SILENCE between one playback ENDING and the next beginning.
	//
	// ⚠️ The gap is measured from the end, never from the start, and that is the whole point.
	// Until 2026-08-31 this was setInterval(play, 4000) — a fixed PERIOD that never consulted the
	// clip. Harmless while every cue was one short word (~1 s clip → ~3 s of quiet), but a
	// principal-parts card's term IS its paradigm ("auftragen, trägt auf, trug auf, aufgetragen",
	// see verbFormsKey), one 4-6 s clip. At a 4 s period the repeat did not merely crowd it:
	// PlSound.play() pauses whatever is sounding (sound.js), so each repeat CUT THE CLIP OFF and
	// restarted it. The learner heard seconds 0-4 on a loop, never the last form, and never a
	// silence to type into — the card was unanswerable by ear.
	//
	// The cap is the other half. The timer allowance scales with the ANSWER's length (1 + 2 s per
	// letter at the default speed), so a paradigm answer used to earn ~15 forced replays over its
	// minute. Three is enough because asking is cheap and already wired: bare Space and the cue's
	// own speaker button both replay (and both feed the replays_before SRS signal, which makes a
	// replay the learner ASKED for a better signal than one imposed on them).
	function startCueLoop(defCluster, td) {
		var gen = ++cueGen;
		var played = 0;
		cueStartedBar = false;

		function playOnce() {
			if (gen !== cueGen) { return; }
			played++;
			var settled = false;
			// Called once per playback, by whichever arrives first: the real 'ended', or the
			// backstop for the cases where it never comes at all (the term has no clip and
			// PlSound.play() just warns, autoplay policy blocked it, a decode error). Without the
			// backstop one silent failure would end the auto-repeat — and, since M5, strand the
			// answer timer that now starts from here.
			function settle(fromEnded) {
				if (settled || gen !== cueGen) { return; }
				settled = true;
				// On EITHER branch — a term with no clip must not leave the card timerless.
				if (played === 1) { beginAllowance(td, fromEnded); }
				if (played < CUE_MAX_PLAYS) { cueTimers.push(setTimeout(playOnce, CUE_GAP_MS)); }
			}
			playSound(defCluster, function () { settle(true); });
			cueTimers.push(setTimeout(function () { settle(false); }, CUE_MAX_MS));
		}

		cueTimers.push(setTimeout(playOnce, CUE_GRACE_MS));
	}

	function stopSound() {
		if (delayedSoundId) {
			clearTimeout(delayedSoundId);
			delayedSoundId = null;
		}
		// Bump first: a timer that already fired and is queued behind this call cannot be cleared,
		// so the generation check inside the loop is what actually stops it re-arming.
		cueGen++;
		for (var i = 0; i < cueTimers.length; i++) { clearTimeout(cueTimers[i]); }
		cueTimers = [];
		window.PlSound.stop();   // silence a word already sounding, not just future scheduled plays
	}

	// Timer tuning from application.yml (learn.exercise.*), inlined into main.html <head> as the
	// exercise_tuning global (see ExerciseProperties). Falls back to the same defaults if absent
	// (e.g. a page that includes this file without the model attribute).
	var tuning = window.exercise_tuning || {};
	function tuningVal(name, dflt) {
		var v = Number(tuning[name]);
		return isFinite(v) && v > 0 ? v : dflt;
	}
	var SPEED_UP_STEP_PERCENT = tuningVal("speedUpStepPercent", 10);
	var SPEED_UP_MAX_STEPS = tuningVal("speedUpMaxSteps", 5);
	var MISS_EXTENSION_PERCENT = tuningVal("missExtensionPercent", 60);
	var MISS_EXTENSION_MAX_FACTOR = tuningVal("missExtensionMaxFactor", 4);
	// Dictation cue timing (startCueLoop). Tuning, not a preference: how long a silence has to be
	// before it reads as "your turn" is a property of listening, not a choice the learner makes.
	var CUE_GRACE_MS = tuningVal("cueGraceMs", 350);      // card arrives -> cue speaks
	var CUE_GAP_MS = tuningVal("cueGapMs", 3000);         // silence between playbacks
	var CUE_MAX_PLAYS = tuningVal("cueMaxPlays", 3);      // total automatic plays; Space replays after
	var CUE_MAX_MS = tuningVal("cueMaxMs", 12000);        // backstop when 'ended' never arrives
	// The reading half of a gapped card's timer (design §5): seconds of allowance per character of
	// the German frame. Tuning, not a preference — how long a frame takes to READ is a property of
	// reading, not a choice the learner makes; the choice they do make (gap or type it all out) is
	// the "Gap long phrases" setting below. 0.05 s/char ≈ 20 characters a second, so a 45-character
	// sentence buys ~2.2 s of reading on top of the time for the word itself.
	var GAP_READ_SEC_PER_CHAR = tuningVal("gapReadSecPerChar", 0.05);

	// Exercise-behaviour settings (pref dropdown) — read live from the DOM, same as #speedValId,
	// so a mid-session change takes effect on the next word.
	function isAdaptiveSpeed() {
		return $("#adaptiveSpeedId").is(":checked");
	}

	function isStrictCaps() {
		return $("#strictCapsId").is(":checked");
	}

	function isDrillArticle() {
		return $("#drillArticleId").is(":checked");
	}

	// Showings-planned (1..3) for THIS drill — the value both isComplete()'s retirement rule and
	// the per-answer event payload read, so the two can never disagree about what governed a run.
	// Since 2026-09-01 the server's verdict reads that same payload too: a word this drill RETIRED
	// is judged against the showings the drill ran, not the notch table's count
	// (VerdictV2.paramsForDrill) — so the tour's pinned 1 is judged as 1, and VOID is left meaning
	// only "the session was dropped before the drill retired the word".
	//
	// THE TOUR PIN (garden-srs-redesign-spec.md §2 slider bullet: "the 3-word onboarding tour
	// alone runs showings=1 so onboarding stays short"). The tour stages its three words through
	// this shared drill and owns no showings control, so it inherits whatever the strictness
	// slider left in #repetitionFoldInId. That is harmless while the shipped default is
	// repetition=1 — and becomes exactly the spec violation above the moment P7's flip 3
	// (learn.srs.notch-five-default) makes notch 5 / showings=2 what a brand-new user starts on,
	// which is the very population the tour exists for: the 3-word first meeting would double to
	// six showings. So the pin rides on that SAME flag (tour_showings_pinned, inlined by
	// main.html from SrsProperties.notchFiveDefault) — flip OFF, this whole branch is dead and
	// every drill is byte-identical to today (the staging law: no shipped behavior changes before
	// the owner's go); flip ON, the tour runs showings=1 at ANY notch, which is what the spec
	// asks for unconditionally. The window is only ever the guided run itself: ONBOARDING clears
	// `running` the moment the finish screen paints, so the user's own first real session — and
	// every session after it — reads the slider again.
	function getRepetition() {
		if (window.tour_showings_pinned
				&& window.ONBOARDING && ONBOARDING.guidedRunLive && ONBOARDING.guidedRunLive()) {
			return 1;
		}
		var v = parseInt($("input[name='repetition']:checked").val(), 10);
		return v === 2 || v === 3 ? v : 1;
	}

	// Phrase-drill answer modes (design §7, settled 2026-07-30). Both were constants while the
	// settings layout was open. ONE switch each, covering all three units — the per-unit matrix the
	// design left as the alternative would have been six checkboxes for what is really one question
	// per direction: is typing the answer worth it here at all.
	// Turning self-check OFF does not disable reverse; it wakes the dormant typing mode (type in
	// full for words, gapped for phrases), which is why nothing in self_check.js was deleted.
	function isSelfCheck() {
		return $("#selfCheckPrefId").is(":checked");
	}

	function isGapPhrases() {
		return $("#gapPhrasesId").is(":checked");
	}

	// Show the subject pronoun of a conjugated-form card as static text, leaving only the verb to
	// type ("ich | fahre", "er, sie, es | fährt"). Applies to conjugation-phrase cards ONLY — the
	// pos the Verbs list gives its expanded forms — because lifting a prefix off any multiword term
	// would maul an ordinary phrase or collocation. Off in reverse mode too: there the answer is
	// English, so there is no German pronoun to hold back.
	//
	// The prefix is matched against the known pronoun labels (Util.conjPronounPrefix), NOT split at
	// the first space: a third-person card is drilled as the whole enumeration, and a naive split
	// would prefill "er," and leave the learner to type "sie, es fährt".
	function applyAnswerPrefix(trainingDef) {
		answerPrefix = "";
		if (trainingDef && !trainingDef.reverse && window.isVerbPronounPrefill && isVerbPronounPrefill()
				&& Util.isVerbFormCard(trainingDef.card)) {
			var term = trainingDef.getTerm();
			// A form with no pronoun ("hilf!") has nothing to lift out — leave it fully typed.
			answerPrefix = Util.conjPronounPrefix(term && term.term);
		}
		answerPrefixField.text(answerPrefix).toggle(!!answerPrefix);
	}

	// Transplant the term's capitalisation onto a (lowercase) search/translit key so strict-caps
	// mode can still accept folded typing: Ärger + "aerger" -> "Aerger". offsets[k] is the key
	// length for term[0..k] (null = keys map 1-to-1), so term[k]'s key span starts at offsets[k-1]
	// (0 for k=0); only the span's first char carries the case (Ä -> "Ae", never "AE").
	function applyTermCase(key, offsets, term) {
		if (!key) {
			return key;
		}
		var out = key.split("");
		for (var k = 0; k < term.length; k++) {
			var ch = term.charAt(k);
			if (ch !== ch.toLowerCase()) {//uppercase letter
				var start = offsets && offsets.length ? (k === 0 ? 0 : offsets[k - 1]) : k;
				if (start < out.length) {
					out[start] = out[start].toUpperCase();
				}
			}
		}
		return out.join("");
	}

	// The gender article the current word must be typed with, or null. Only while the "Nouns with
	// article" setting is on, and from two different places depending on the language:
	//
	//   faceArticle      the SERVER's per-sense card face (rule F9) — Spanish's `el agua`, computed
	//                    from the sense's gender and the term's own transcription, because Spanish
	//                    has minimal pairs on gender (`el capital` money, `la capital` city) and
	//                    cannot use a per-TERM column at all. Wins where it exists.
	//   termDto.article  the stored per-term column — German's der/die/das, untouched.
	function articleFor(termDto, faceArticle) {
		var article = faceArticle || termDto.article;
		return isDrillArticle() && article ? article : null;
	}

	// Rule F9's accepted-article set — `el/la` is one face and both halves are right. The rule
	// itself lives in grader_policy.js beside the strictness half (the face and the spellings
	// accepted for it are ONE decision); with the module absent the face is accepted whole, which
	// is the behaviour this file had before F2b.
	function articleAlternatives(article) {
		if (window.GraderPolicy) {
			return GraderPolicy.articleAlternatives(article);
		}
		return article ? [article] : [];
	}

	// The accepted "typed umlaut substitution" spelling, derived STRAIGHT FROM the display term:
	// ä→ae, ö→oe, ü→ue, ß→ss, keeping spaces, case and punctuation (Ü→Ue, never UE, matching
	// applyTermCase). The server's termSearch/termSearchFold keys can't be used for this: they
	// double as the dictionary prefix key and so strip spaces AND punctuation (NonAsciiCharRemover),
	// which is invisible for a single word but collapses a multi-word term/phrase to one run
	// ("ich interessiere mich für Musik." → "ichinteressieremichfuermusik") — a form a phrase typed
	// with spaces can never match. Deriving from the term fixes BOTH the answer check and the
	// wrong-answer diff for phrases. German only (ü→ue is a German rule; other languages fold
	// accents to a single letter, already carried by termSearch for single words).
	function umlautSubstForm(termDto) {
		if (termDto.languageCode !== 'DE') {
			return null;
		}
		return Util.umlautSubst(termDto.term);
	}

	// Whether the server's search keys may be offered as accepted spellings of THIS language's
	// words — grader_policy.js, which owns every per-language rule that makes the forward grader
	// stricter. Absent module (a page that loads the drill without it) grades as the app always
	// did: the policy only ever narrows, so falling back to "yes" is the safe direction.
	function searchKeysAreSpellings(langCode) {
		return window.GraderPolicy ? GraderPolicy.searchKeysAreSpellings(langCode) : true;
	}

	// Every spelling accepted for the term under the CURRENT settings — the single source for
	// both the answer check (processUserAnswer) and the wrong-answer diff (answerWasWrong).
	// Strict caps: the exact term + the case-transplanted translit/fold keys (Ärger ->
	// "Ärger"/"Aerger"/"Arger"); lenient: term + raw keys (callers add a case-insensitive pass).
	// With an article required, every form is prefixed "der " etc. and the bare noun is
	// deliberately NOT in the list — typing the article is the point. The article itself stays
	// lowercase even in strict caps (der Kunde, never Der Kunde).
	function acceptedForms(termDto, faceArticle) {
		var forms = [termDto.term];
		function add(f) {
			if (f && forms.indexOf(f) < 0) {
				forms.push(f);
			}
		}
		// The server's search keys are offered for a SINGLE-WORD term only. They double as the
		// dictionary prefix key, so they strip spaces and punctuation (NonAsciiCharRemover): for
		// "auftragen, trägt auf, trug auf, hat aufgetragen" the key is the one unreadable run
		// "auftragentraegtauftrugaufhataufgetragen". As an accepted spelling it is dead weight —
		// looseKey turns every separator into a space, so no answer typed with word boundaries can
		// ever equal it — but AnswerDiff.closest still ranks it, and on a badly-missed card it wins
		// and the reveal shows the correct answer with no word separation at all (owner report
		// 2026-08-25). A multi-word term's folded spelling comes from umlautSubstForm below, which
		// keeps the separators. Whitespace is the test, not punctuation: a hyphen or apostrophe
		// inside one word ("Ich-Erzähler", "geht's") still reads fine collapsed.
		if (!/\s/.test(termDto.term || "") && searchKeysAreSpellings(termDto.languageCode)) {
			if (isStrictCaps()) {
				add(applyTermCase(termDto.termSearch, termDto.searchOffsets, termDto.term));
				add(applyTermCase(termDto.termSearchFold, termDto.foldOffsets, termDto.term));
			} else {
				add(termDto.termSearch);
				add(termDto.termSearchFold);
			}
		}
		// Space-preserving umlaut substitution derived from the term itself — the only accepted
		// form that survives multi-word terms (see umlautSubstForm). Carries the term's own case,
		// so it is valid for the strict-caps exact pass; the lenient case-insensitive pass covers
		// the rest. No-op for a term with no umlaut (dedupes against the term already in the list).
		add(umlautSubstForm(termDto));
		var article = articleFor(termDto, faceArticle);
		if (article) {
			// The face goes FIRST so AnswerDiff.closest aligns a near-miss against the spelling the
			// card actually showed, and each alternative article is offered on every spelling.
			var articles = articleAlternatives(article), prefixed = [];
			for (var a = 0; a < articles.length; a++) {
				for (var i = 0; i < forms.length; i++) {
					var withArticle = articles[a] + " " + forms[i];
					if (prefixed.indexOf(withArticle) < 0) { prefixed.push(withArticle); }
				}
			}
			forms = prefixed;
		}
		return forms;
	}

	// The first sense's own gloss, off the cluster. A TERM card's `gloss` is the COMPOSED one (the
	// translations, one line), so a card that wants the sense's disambiguator has to come here for
	// it — used by the principal-parts cue, whose whole reading depends on it.
	// Rule F9's card FACE article for this card, off the cluster, or "" when the language has none.
	//
	// ⭐ Sense-grained on purpose, and Spanish is why: `el capital` is money and `la capital` is a
	// city — ONE term, two genders, two faces. So a card that names its own sense gets THAT sense's
	// article, and only a card that names none falls back to the first. ⛔ Never stamped onto the
	// TermDto: one term object is shared by every card built from it, and a card drilling the
	// masculine sense would overwrite the feminine one's face.
	function senseFaceArticle(defCluster, card) {
		var defs = (defCluster && defCluster.definitionList) || [];
		var wanted = (card && card.senseIds) || (card && card.senseId != null ? [card.senseId] : []);
		var first = "";
		for (var i = 0; i < defs.length; i++) {
			var groups = defs[i].senseGroups || [];
			for (var j = 0; j < groups.length; j++) {
				if (!groups[j].article) { continue; }
				if (wanted.length && wanted.indexOf(groups[j].senseId) >= 0) {
					return groups[j].article;
				}
				if (!first) { first = groups[j].article; }
			}
		}
		return first;
	}

	function firstSenseGloss(defCluster) {
		var defs = (defCluster && defCluster.definitionList) || [];
		for (var i = 0; i < defs.length; i++) {
			var groups = defs[i].senseGroups || [];
			for (var j = 0; j < groups.length; j++) {
				if (groups[j].gloss) { return groups[j].gloss; }
			}
		}
		return "";
	}

	// The instruction line under a form-set cue: "irregular verb — 3 forms". Without it the card
	// shows a meaning and a text box like every other, and nothing says three words are wanted —
	// and since German gained form-set cards it is also the only thing telling this card apart from
	// the same verb's conjugated-form card, which is cued identically.
	//
	// The count is DERIVED from the card, never fixed: can/could has no participle (2), be/have/do/say
	// carry an irregular 3rd person (4), every other English verb is 3, and every German card is 4 —
	// and a card whose heading disagrees with the forms under it reads as missing data. Counted with
	// verbFormsCount, which splits on the SEPARATOR: token-counting made every phrasal verb ("bear
	// out, bore out, borne out" — three forms, six words) claim six, which is most of the English
	// list, and it would do the same to German's "ist gefahren".
	//
	// 2, 3 and 4 are all reachable, and one string serves each: Russian's "формы" is the right form
	// for all three counts (5+ would need "форм", which cannot occur). One string covers both
	// languages too — "irregular" is exactly what the German membership predicate selects, and
	// "strong" would be wrong for the mixed verbs and modals it also holds (brennen, müssen).
	function verbFormsHintHtml(termDto) {
		var n = Util.verbFormsCount(termDto && termDto.term);
		if (n < 2) {
			return "";   // a malformed card says nothing rather than "1 forms"
		}
		return "<span class='_verbFormsHintClass'>"
			+ strFormat(getRes("tsVerbForms_hint") || "irregular verb — {0} forms", n) + "</span>";
	}

	// The tense a conjugated card drills, named in the learner's own textbook term («перфект») off
	// the one definition in Util; the German original + descriptive name ride the data-tense
	// tooltip (Util.conjTenseTip, delegated once in main.js). Stamped on the deck at Start by
	// main.js's stampVerbTenses — Practice's card payload carries no paradigm data of its own, and
	// a card must never wait on a lookup mid-drill.
	//
	// This is the line that makes such a card answerable at all in a pair whose prompt language has
	// one past: DE→RU glosses «я говорил» for both "ich sprach" and "ich habe gesprochen", so the
	// meaning alone asks a question with two right answers. Absent tense → absent line, which is
	// simply the behaviour before 2026-08-05.
	function verbTenseHintHtml(card) {
		if (!Util.isVerbFormCard(card) || !card.verbTense) {
			return "";
		}
		return "<span class='_verbTenseHintClass' data-tense='" + escHtml(card.verbTense) + "'>"
			+ Util.conjTenseLine(card.verbTense) + "</span>";
	}

	// ── conjugated-form cards: the head verb's senses, never the cell's own ───────────────────────
	//
	// A paradigm cell is its own term row carrying exactly ONE stored translation, chosen per cell
	// when the corpus was minted — and that choice is unsound by construction: sense 1 of `abbrennen`
	// takes an inanimate subject, so eight of its twelve cells have no literal reading and the model
	// filled them with an idiom that belongs to a different lemma. One paradigm ended up teaching two
	// different verbs (docs/project/conj-card-and-answer-matching.md §2).
	//
	// The rule that replaced it (§3): a cell's gloss is a LABEL FOR A SLOT, never lexicography. The
	// meaning comes from the head verb's own senses; the form is named by a grammar label. A cell
	// never chooses a sense — so nothing below ever reads card.translations / card.gloss for a
	// conjugated card, in either direction.
	//
	// The senses ride the deck from stampVerbTenses (main.js) — Practice has no card payload of its
	// own to carry them. A card that arrives without them (lookup failed, or a head with no taught
	// verb sense) keeps today's behaviour rather than showing an empty prompt, which is why every
	// caller here goes through headSensesOf and falls through when it returns null.

	/** How many senses the CUE shows. The payload carries all of them — see acceptedHeadTranslations. */
	var CONJ_SENSES_SHOWN = 4;

	// Minimal escaper for the strings this block interpolates. The rest of this file concatenates
	// corpus text into HTML unescaped, which has been survivable because the corpus is ours; a
	// translation containing "&" or "<" is still a rendering bug, and new markup should not add more
	// of them.
	function escHtml(s) {
		return String(s == null ? "" : s)
			.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
			.replace(/"/g, "&quot;").replace(/'/g, "&#39;");
	}

	/** The head verb's senses for a conjugated card, or null when it has none to show. */
	function headSensesOf(card) {
		var senses = Util.isVerbFormCard(card) && card.headSenses;
		return (senses && senses.length) ? senses : null;
	}

	// The meaning block: the head verb's senses, numbered, in entry order, each sense's translations
	// joined the way every other sense line in the app joins them. Numbered because there are several
	// and they are alternatives, not one meaning — and because the ask ("any of these will do") reads
	// as a list.
	function headSensesHtml(senses) {
		var n = Math.min(senses.length, CONJ_SENSES_SHOWN);
		var h = "<ol class='_conjSensesClass'>";
		for (var i = 0; i < n; i++) {
			h += "<li><span class='_conjSenseNumClass'>" + (i + 1) + ".</span>"
				+ "<span>" + escHtml((senses[i] || []).join(" · ")) + "</span></li>";
		}
		return h + "</ol>";
	}

	// The grammar label that names WHICH cell: the tense, and the person. Both in the learner's own
	// language — the German subject pronoun would be the first word of the answer they are about to
	// type, so naming the person with it would give the card away. (Learn's comprehension rung is the
	// one screen where the pronoun IS the right name for the person, and it has the pronoun off the
	// cue precisely so it can ask for it.)
	function conjSlotHtml(td) {
		var h = "";
		if (td.card.verbTense) {
			h += "<span class='pl-chip _conjChipClass' data-tense='" + escHtml(td.card.verbTense) + "'>"
				+ escHtml(Util.conjTenseLine(td.card.verbTense)) + "</span>";
		}
		var person = Util.conjPersonLine(td.getTerm() && td.getTerm().term);
		if (person) {
			h += "<span class='pl-chip _conjChipClass'>" + escHtml(person) + "</span>";
		}
		return h ? "<div class='_conjSlotClass'>" + h + "</div>" : "";
	}

	// The card type in words ("Verb form"), in place of the 5-character POS abbreviation every other
	// cue wears. The abbreviation is a gutter label for a dictionary row; on a card whose whole
	// question is "which slot of which verb is this", saying what kind of card it is belongs on the
	// face. Text comes from the pos vocabulary already wired up in step 2 (pos_label.js), so it is
	// locale-driven with no new key.
	function conjKindHtml() {
		var kind = PosLabel.tooltip(Util.VERB_FORM_POS);
		return kind ? "<div class='_conjKindClass'>" + escHtml(kind) + "</div>" : "";
	}

	// Every translation the HEAD VERB carries, flattened — the reverse card's accepted-answer pool.
	// Uncapped on purpose, unlike the cue: the cue shows four senses because that is what a pane
	// holds, and grading a fifth sense's translation wrong would be a defect, not a display choice.
	function acceptedHeadTranslations(senses) {
		var out = [];
		for (var i = 0; i < senses.length; i++) {
			for (var j = 0; j < (senses[i] || []).length; j++) {
				if (senses[i][j] && out.indexOf(senses[i][j]) < 0) { out.push(senses[i][j]); }
			}
		}
		return out;
	}

	// The form row's own translations FIRST, then the head's — order is not cosmetic here. The
	// wrong-answer diff aligns what was typed against the NEAREST accepted spelling
	// (AnswerDiff.closest), and the form's own line is the morphologically right answer, so it must
	// be the one a near-miss is measured against rather than an infinitive that happens to be close.
	function unionTranslations(own, extra) {
		var out = own.slice();
		for (var i = 0; i < extra.length; i++) {
			if (extra[i] && out.indexOf(extra[i]) < 0) { out.push(extra[i]); }
		}
		return out;
	}

	// The language a REVERSE card is answered in: the meaning side of the pair (langFrom), which
	// is what the typist has to produce. Read off the DOM like every other pair read in this file
	// so a pair switch needs no re-wiring, and passed to AnswerForms — which tolerates a language
	// it has no rules for by matching on its neutral core.
	function answerLangCode() {
		return ($("#langFromValId").attr("data-lang-code") || "").toUpperCase();
	}

	// Accepted spellings for a card under ITS OWN direction. Forward: the term's spellings (case
	// rules, article prefix, umlaut substitutions) — recomputed each time because it reads live
	// settings. Reverse: every translation the WHOLE term carries (the bare-word cue can't say
	// which meaning is asked, so a sibling sense's translation is a right answer too), expanded
	// for optional markers/qualifiers by answer_forms.js under the answer language's rules —
	// settings-independent, cached on the card.
	function formsFor(td) {
		if (!td.reverse) {
			// A gapped card asks for the focus word alone, so the accepted spellings are ITS
			// spellings, run through the very same case / umlaut-substitution rules.
			var plan = td.getGapPlan();
			if (plan) {
				return acceptedForms(gapAnswerDto(td, PhraseGap.expected(plan)))
					.concat(gapStoredOrderForms(td, plan));
			}
			var forms = acceptedForms(td.getTerm(), td.getFaceArticle());
			// A principal-parts card also offers its forms separated by spaces alone. The answer
			// CHECK does not need this — it compares word by word (verbFormsKey) and already accepts
			// any separator — but the wrong-answer DIFF aligns character by character against the
			// NEAREST accepted spelling, and with only the comma-joined face to align against, a
			// learner who typed "go went gonne" would see every missing comma marked alongside their
			// one real mistake. Offering both spellings lets AnswerDiff.closest pick the one they
			// actually wrote, so the diff points at the wrong FORM instead of at the punctuation.
			if (Util.isVerbFormsCard(td.card)) {
				var spaced = Util.verbFormsTokens(td.getTerm().term).join(" ");
				if (spaced && forms.indexOf(spaced) < 0) { forms.push(spaced); }
			}
			return forms;
		}
		if (!td.reverseForms) {
			var translations = td.getTermTranslations();
			td.reverseForms = window.AnswerForms
				? AnswerForms.accepted(translations, answerLangCode())
				: translations.slice();
		}
		return td.reverseForms;
	}

	// The cluster's plain definition text — what a pair with no rich senses carries INSTEAD of
	// sense translations, and the only meaning such a card has to show.
	function definitionTextOf(defCluster) {
		var parts = [];
		var defs = (defCluster && defCluster.definitionList) || [];
		for (var i = 0; i < defs.length; i++) {
			if (defs[i].definition) { parts.push(defs[i].definition); }
		}
		return parts.join(" | ");
	}

	// A term-shaped stand-in for what a gapped card actually asks to be typed: the focus form(s),
	// nothing else. Deliberately carries NO search keys — the server's termSearch/termSearchFold are
	// built for the whole phrase and strip spaces and punctuation — and no article, so acceptedForms
	// contributes exactly the form itself plus its umlaut substitution, which is the full set a
	// typed focus word needs.
	function gapAnswerDto(td, text) {
		return {term: text, languageCode: td.getTerm().languageCode};
	}

	// Also accept the focus form in the order it was STORED, when that differs from reading order.
	// The matcher emits its parts in candidate order (for a separable verb, stem then particle —
	// main-clause order), which is reading order in every case the corpus produces. Accepting both
	// costs one extra form and removes a whole class of surprise if a clause ever inverts them.
	function gapStoredOrderForms(td, plan) {
		var stored = PhraseGap.parts(td.getTerm().focusForm).join(" ");
		return stored && stored !== PhraseGap.expected(plan)
			? acceptedForms(gapAnswerDto(td, stored)) : [];
	}

	// Strict caps is a German-orthography setting (noun capitalization) — English answers are
	// always compared case-insensitively. Per card: in a Mix session the forward half still
	// honours the setting.
	function isCaseStrict(td) {
		return td && td.reverse ? false : isStrictCaps();
	}

	// Loose comparison key for the answer check: what is graded is the WORDS, not the punctuation
	// between them. Apostrophes (straight, curly or modifier) drop out entirely, so geht's == gehts;
	// every OTHER run of non-letters — commas, periods, hyphens, slashes, runs of spaces — becomes a
	// single space. A card face's separators are presentation ("er, sie, es hat angebracht" prints
	// its commas because a paradigm table does), and the learner is being asked for the forms, so
	// "er sie es hat angebracht" and "er,sie,es hat angebracht" are the same answer as the face.
	//
	// Punctuation becomes a SPACE rather than nothing, so word boundaries still count and "ersiees"
	// stays wrong — and a form with a hyphen inside it is answerable either way ("Ich-Erzähler" ==
	// "Ich Erzähler") without the two words melting together. Letter case is left untouched here —
	// strict-caps stays exact-case; the lenient path adds its own case fold. A future "strict" mode
	// can bypass this. Only the correctness check uses it; the wrong-answer diff keeps real spelling.
	function looseKey(s) {
		return s
			.replace(/['‘’ʼ]/g, "")                    // drop apostrophes (straight / curly / modifier)
			.replace(/[^\p{L}\p{M}\p{N}]+/gu, " ")   // every other separator run -> one space
			.trim();
	}

	// The comparison key for a PRINCIPAL-PARTS card — the forms as bare words, in order, joined by
	// single spaces. The card face reads "go, went, gone" because that is how a verb table prints,
	// but what is being asked for is three FORMS, so every way of separating them is right:
	// "go went gone", "go / went / gone", a trailing comma, a double space. Order still matters —
	// the three are a paradigm, not a set.
	//
	// Case-insensitive whatever the strict-caps setting says (Util.verbFormsTokens lowercases):
	// strict caps is a German-orthography rule and has nothing to say about an English verb form.
	function verbFormsKey(s) {
		return Util.verbFormsTokens(s).join(" ");
	}

	this.startSession = function () {
		if (Util.isEmpty(toShow)) {
			throw Util.exception("tsWordNotRetrieved");
		}
		this.clearSession();
		sessionStartTs = Date.now();
		doc.setCounterText(toShow.length);
		initialWordCount = toShow.length;
		doc.setOverallProgress(0);
		shuffle();
		startNewCycle();
		setNextDefinition();

		var startParams = {
			langFrom: sessionMeta.langFrom || '',
			langTo: sessionMeta.langTo || '',
			origin: sessionMeta.origin || 'RANDOM',
			termCount: toShow.length,
			speed: parseInt($("#speedValId").text()) || 4,
			soundOnly: sessionAudioCue,
			// SESSION grain (user_session.direction, a SessionDirection): the shape of the whole
			// session, not of one card. A Mix session is MIXED — its real directions live on the
			// event rows. Pure reverse stays TERM_TO_MEANING whether or not the cue was audio;
			// audio + forward is DICTATION.
			direction: sessionMixed ? 'MIXED'
				: sessionReverse ? 'TERM_TO_MEANING'
				: sessionAudioCue ? 'DICTATION' : 'MEANING_TO_TERM'
		};
		// The browser's IANA zone, persisted on the session (0042) — ground truth for
		// local-time analyses; the same expression the stats call already sends.
		try {
			startParams.tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
		} catch (e) { /* stats must never break the drill */ }
		if (sessionMeta.wordListId) startParams.wordListId = sessionMeta.wordListId;
		startParams.sessionKey = sessionKey;
		// The §4.3 floor constants normally arrive at page load; retry here if that fetch lost
		// (e.g. it raced the login session) so at worst the FIRST session runs floorless.
		if (!plMotorParams) {
			plFetchMotorParams();
		}
		postStats("api/exercise-session-start", startParams, function (data) {
			if (data && data.sessionId) { sessionId = data.sessionId; }
		});
	};

	this.clearSession = function () {
		stopSound();
		hideRevealWord();
		hideContinueAction();
		applyTypeSubst(null);   // no card, no answer language, no mapping
		answerBox.removeAttr("data-answer-lang");
		trainingDef = null;
		doc.setResultText("");
		doc.setAnswerDiff("");
		doc.setTipText("");
		doc.setCounterText("");
	};

	this.isSessionStopped = function () {
		return isSessionStopped;
	};

	// The session end's parameters: always the key, plus the server's number when it came back.
	function endParamsFor(outcome) {
		var p = { sessionKey: sessionKey, outcome: outcome };
		if (sessionId) { p.sessionId = sessionId; }
		return p;
	}

	this.stopSession = function () {
		this.clearSession();
		progressBar.stop();
		progressBar.clear();
		isSessionStopped = true;
		{
			// Waits like the finish path does: a stopped session now gets the garden advance too,
			// for exactly the words it managed to retire, and that answer has to be read off a
			// complete trail of card posts. The id is captured before the wait, the way the finish
			// path captures its whole param object — the callback must end THIS session whatever
			// the user starts in the meantime. No longer gated on the server's number: the session
			// key names the session even when the start's reply never came back.
			var endParams = endParamsFor('STOPPED');
			whenCardPostsSettled(function () {
				postStats("api/exercise-session-end", endParams, function () {
					if (typeof window.refreshStats === 'function') {
						window.refreshStats();
					}
				}, true);
			});
		}
	};

	this.pauseSession = function () {
		stopSound();
		progressBar.pause();
		isSessionStopped = true;
	};

	this.resumeSession = function () {
		isSessionStopped = false;
		// Paused while the dictation cue was still speaking: the allowance had not started yet
		// (startCueLoop starts it) and pauseSession's stopSound killed the loop that would have.
		// Say the cue again from the top rather than resume() a bar that was never start()ed —
		// ProgressBar.resume is a bare setInterval over whatever _trainingDef it was last given,
		// which here is the PREVIOUS card, so it would run this card down on the wrong allowance.
		if (sessionAudioCue && trainingDef && !cueStartedBar && !correctAnswerShown) {
			startCueLoop(trainingDef.getDefCluster(), trainingDef);
			return;
		}
		progressBar.resume();
	};

	// A miss (or a timeout) reveals this card's FULL rich entry — the moment the learner most wants
	// to read it, and until now the moment they wait on a "Looking up…" strip. Warm it while the card
	// is still being answered, so the reveal renders from cache. The sound sibling of this is the
	// batch warm-up in the initializer above; this one is per-card because a drill deck is long and
	// warming it whole would fire a burst of fat entry requests for words most of which are answered
	// right and never reveal anything.
	//
	// Gated on senseIds: those senses ARE stored rich data, so the lookup is a plain DB read. A
	// sense-less legacy card, or a phrase (which main.js never rich-looks-up anyway), would instead
	// mean a paid AI generation for a word the learner may well get right — not something a
	// speculative prefetch is allowed to buy.
	function prefetchCardRichEntry(td) {
		if (!window.prefetchRichEntry || !td || !td.getSenseIds().length) { return; }
		var term = td.getDefCluster().term;
		if (!term || term.phrase || !term.term) { return; }
		window.prefetchRichEntry(term.term);
	}

	var setNextDefinition = function () {
		correctAnswerShown = false;
		hideContinueAction();   // no answer on screen any more — the pill has done its job
		trainingDef = getNextTrainingDef();
		// Before the cue is rendered below: a card seen again after its answer was revealed must
		// come back blank, not still filled in.
		if (trainingDef) { trainingDef.gapRevealed = false; }

		stopSound();

		if (trainingDef == null) {
			finishSession();
			return;
		}

		if (sessionAudioCue) {
			makeSoundOnly(true);
			var defCluster = trainingDef.getDefCluster();
			setTimeout(setNextDefinition_continue, 250);
			// The field is ready at +250 ms (above) but the CLOCK is not started there — under
			// dictation startCueLoop starts it when the cue stops speaking.
			startCueLoop(defCluster, trainingDef);
		} else {
			doc.setDefExerciseText(trainingDef.getDefinition());
			addCueSound();
			// Reverse doubles as listening practice: the German cue auto-pronounces on
			// arrival (same grace delay as sound-only; stopSound clears a pending one).
			if (trainingDef.reverse) {
				delayedSoundId = setTimeout(function () {
					playSound(trainingDef.getDefCluster());
				}, 350);
			}
			setNextDefinition_continue();
		}
	};

	function setNextDefinition_continue() {
		doc.setAnswerText("");
		applyAnswerPrefix(trainingDef);   // static pronoun for this card, or cleared
		doc.setAnswerDiff("");
		hideRevealWord();   // fresh word — the reveal button re-earns its place after two misses
		// "Type the translation in {0}": the answer language — langTo normally, langFrom
		// (e.g. English) when THIS CARD is reversed (it changes per card in a Mix session).
		// …named in the READER's language, not as the endonym the picker shows
		// (docs/project/i18n-runtime-grammar.md §4.1) — this splices straight into "Type the
		// correct {0} translation here", which is a sentence in the reader's language.
		var $answerLangField = $(trainingDef && trainingDef.reverse ? "#langFromValId" : "#langToValId");
		var answerLang = langDisplayName($answerLangField.attr("data-lang-code"),
			getFirstText($answerLangField));
		// …and on a MONOLINGUAL pair the word "translation" is simply wrong: the learner is typing
		// the English word itself, cued by its English defining phrase. Same identity test
		// main.js's isSameLangPair() uses — read off the two selects rather than from a pair list,
		// so nothing about the pair config is duplicated into the client.
		var fromCode = $("#langFromValId").attr("data-lang-code");
		var toCode = $("#langToValId").attr("data-lang-code");
		var sameLangPair = !!fromCode && fromCode === toCode;
		doc.setAnswerPlaceholder(strFormat(
			getRes(sameLangPair ? "tsTypeWord_tip" : "tsTypeTranslation_tip"), answerLang));
		// …and recorded where the guided walk's answer-field step can read it (onboarding_guide.js
		// answerLanguage). That step names the same language the placeholder just named, and it has
		// no way of its own to know which way THIS card runs.
		answerBox.attr("data-answer-lang", answerLang);
		applyTypeSubst(trainingDef);   // …and how to type that language's own letters (ä→ae …)
		doc.setResultText("");
		if (window.resetExerciseRichEntry) { window.resetExerciseRichEntry(); }

		if (trainingDef) {
			trainingDef.presentedCount++;
			trainingDef.shownAtTs = Date.now();
			trainingDef.wrongEnterCountThisPresentation = 0;
			trainingDef.lastWrongKey = null;   // fresh presentation → the next wrong answer is "new"
			trainingDef.attemptsThisPresentation = [];   // R39: this showing's tracked attempts, fresh
			// 0042 per-presentation instrumentation: one uuid per SHOWING (shared by every attempt
			// + lockstep sense row), first-key/typing counters reset per presentation NOT per
			// attempt, pre-answer replay count, and whether the cue auto-pronounced (sound-only
			// autoplay + reverse's arrival pronunciation — exactly the two auto-play paths above).
			trainingDef.presentationId = Util.uuid();
			trainingDef.firstKeyMs = null;
			trainingDef.keyCount = 0;
			trainingDef.backspaceCount = 0;
			trainingDef.longestPauseMs = 0;
			trainingDef.lastKeyTs = null;
			trainingDef.replaysBefore = 0;
			trainingDef.autoSoundPlayed = sessionAudioCue || trainingDef.reverse;
			// M5 per-presentation capture state (§4.1/§4.4): the edit-timeline segment of the
			// current attempt, input-mode marks, absolute first-key instant and the dictation
			// audio-end instant its clock starts from.
			trainingDef.tlEntries = [];
			trainingDef.tlDiverged = false;
			trainingDef.tlInBackspaceRun = false;
			trainingDef.tlPasted = false;
			trainingDef.tlComposed = false;
			trainingDef.firstKeyAbsTs = null;
			trainingDef.audioEndTs = null;
			prefetchCardRichEntry(trainingDef);
		}

		showSelfCheck(trainingDef);
		// Under dictation the allowance starts when the CUE STOPS SPEAKING, not here — startCueLoop
		// calls this instead. It used to start at +250 ms, i.e. 100 ms BEFORE the audio even began:
		// invisible while a cue was one short word, plainly wrong once a principal-parts clip runs
		// 4-6 s of a ~19 s allowance. Dictation gets no compensation for it anywhere else either —
		// the L* floor passes cueLen = 0 for an audio cue (see getAnswerTime), so the per-character
		// term that pays a reader for reading their cue drops out entirely for a listener.
		if (!sessionAudioCue) { progressBar.start(trainingDef); }
		$("#startId").prop("disabled", false);//enable pause button
		// A self-check card has no field to type into, so focus stays off it — but the pane must
		// still hold focus or the digit and Enter shortcuts have nothing to fire on.
		if (trainingDef && trainingDef.selfCheck) { $("#paneId").focus(); } else { answerField.focus(); }
	}

	// ── reverse self-check (design §6) ───────────────────────────────────────────
	// The passive direction asks with buttons instead of the answer field: typing English is a
	// spelling test in a language the learner already speaks, so what is tested is recognition.
	// The step machine and the timer formula live in self_check.js; everything below is the DOM,
	// the events and the SRS posting.
	//
	// §7 landed 2026-07-30: the switch is the "Self-check instead of typing" preference (isSelfCheck
	// above), read per card off the DOM, so flipping it mid-session takes effect on the next word.
	// It was a constant until then.
	var selfCheckBox = $("#selfCheckId");

	function showSelfCheck(td) {
		var on = !!(td && window.SelfCheck
			&& SelfCheck.applies({reverse: td.reverse, audioCue: sessionAudioCue}, isSelfCheck()));
		if (td) { td.selfCheck = on; td.selfStep = on ? SelfCheck.STEP_COMMIT : null; }
		// The answer field and its affordances are meaningless without typing — hide the whole
		// box rather than leaving a dead input the learner might click into. The typing mapping
		// goes with it: hidden outright while the buttons stand there, and REBUILT rather than
		// merely re-shown on the way back, since whether it belongs on screen at all is a question
		// about this card's answer language, not about what it was doing before.
		answerBox.find("label").toggle(!on);
		// The reveal button is NOT in that toggle any more. Its visibility is class-driven, and
		// jQuery .show() on an element the stylesheet hides writes an inline display instead —
		// so every card that ran this with self-check off was forcing the button VISIBLE, from
		// the first word, months before it had been earned. Nobody noticed until the typing
		// mapping came to share that edge and got clipped by a button that should not be there.
		// Hiding it when the buttons take over is all this line ever needed to do.
		if (on) {
			hideRevealWord();
			answerBox.removeClass("hasTypeSubstClass");
		} else {
			applyTypeSubst(td);
		}
		if (!on) { selfCheckBox.hide().empty(); return; }
		renderSelfCheckStep(td);
	}

	function renderSelfCheckStep(td) {
		var opts = SelfCheck.buttons(td.selfStep);
		selfCheckBox.empty().css("display", "flex");
		for (var i = 0; i < opts.length; i++) {
			selfCheckBox.append(selfCheckButton(td, opts[i]));
		}
	}

	function selfCheckButton(td, opt) {
		// The digit badge is the Learn ladder's idiom, carried over verbatim so the two modes'
		// keyboards feel like one app: the number shown IS the key that presses it.
		return $("<button type='button'></button>")
			.addClass("pl-btn-pill _selfBtnClass " + opt.cls)
			.attr("data-self-key", opt.key)
			.append($("<span class='_selfNumClass'></span>").text(opt.n))
			.append($("<span></span>").text(getRes(opt.res)))
			.on("click", function () { pressSelfCheck(td, opt.key); });
	}

	/**
	 * One button press. Step 1's "I understand" is the only press that reveals without deciding —
	 * every other press ends the card. The COMMIT-before-reveal order is the design's whole
	 * argument and must survive refactoring: revealing first lets a learner tell themselves they
	 * knew it.
	 */
	function pressSelfCheck(td, key) {
		if (!td || td !== trainingDef || !td.selfCheck || correctAnswerShown || isSessionStopped) { return; }
		if (!defReturned) { return; }
		var r = SelfCheck.press(td.selfStep, key);
		if (!r.done) {
			// Step 1 → step 2. The clock covered thinking and stops here (§6.1): grading yourself
			// is not a timed task, and timing it would reward the fast lie over the honest pause.
			progressBar.stop();
			td.selfStep = r.next;
			td.gapRevealed = true;
			doc.setDefExerciseText(td.getDefinition() + td.getRevealAnswerHtml());
			addRevealSound();   // the answer is up and this card has no rich entry — offer to hear it
			renderSelfCheckStep(td);
			return;
		}
		var completionMs = td.shownAtTs ? Date.now() - td.shownAtTs : null;
		postPresentationEvent(td, {correct: r.pass, submitted: null, completionMs: completionMs});
		postAnswerEvent(td, r.pass ? 'PASS' : 'FAIL', null);
		if (r.pass) {
			selfCheckPassed(td);
		} else {
			// Both failures — "I don't understand" and the confidently-wrong "I got it wrong" —
			// land here and are worth the same. Nothing is discounted on the fail side.
			answerWasWrong({posted: true});
		}
	}

	// The correct-answer bookkeeping, minus everything that only makes sense for typed text (the
	// timeline commit, the answer string, the wrong-answer diff). Mirrors processUserAnswer's
	// success arm — kept beside it in behaviour, not merged into it, because that function is
	// entered from the answer field and reads it throughout.
	function selfCheckPassed(td) {
		td.correctCount++;
		if (td.presentedCount === 1 && td.wrongEnterCountThisPresentation === 0) {
			td.firstTryCorrect = true;
		}
		if (td.shownAtTs) {
			td.totalResponseMs += (Date.now() - td.shownAtTs);
			td.shownAtTs = null;
		}
		setAnswer(true);
		if (td.isComplete()) {
			var counter = Number(doc.getCounterText());
			doc.setCounterText(counter - 1);
			doc.setOverallProgress(initialWordCount
				? (initialWordCount - (counter - 1)) / initialWordCount : 0);
		}
		setNextDefinition();
	}

	var getNextTrainingDef = function () {
		if (defReturned) {
			throw Util.exception("tsAnswerNotSet");
		}
		var currentEntry = null;
		var toShowSize = toShow.length;
		if (toShowSize > 0) {//session continues
			var stillWordsInCycle = true;
			if (currIndex == toShowSize) {//cycle ended
				stillWordsInCycle = startNewCycle();
			}
			if (stillWordsInCycle) {
				currentEntry = toShow[currIndex];
			}
		}//else, session ended, return null
		defReturned = true;
		return currentEntry;
	};

	var finishSession = function () {
		doc.setDefExerciseText("");
		doc.setAnswerText("");
		applyAnswerPrefix(null);   // no card on screen — drop any lingering pronoun
		doc.setAnswerDiff("");
		doc.setResultText("");
		progressBar.stop();
		progressBar.clear();
		progressBar.hide();

		// No manual resize on finish: the two progress-bar tracks stay in place (only the timer
		// fill hides), so the chrome height is unchanged — the word box must NOT jump. The overall
		// bar is already full at 100% from the last mastered word, a natural "done" state.

		// The finish screen (summary card, Practice Again / Back to Words buttons) lives in main.js —
		// this is its bridge, same pattern as showExerciseRichEntry. The server response then fills
		// the summary's tricky-words row + overall-progress bridge line via exerciseSessionEnded.
		if (window.exerciseFinished) {
			window.exerciseFinished(computeSummary());
		} else {
			$("#startId").prop("disabled", true);
			doc.setResultText("<span id='resultLabel' style='color:var(--pl-accent-vivid)'>" +
			getRes("tsYouDoneIt") +
			"</span><span id='exerciseFinished'></span>");
		}

		{
			var endParams = endParamsFor('COMPLETED');
			whenCardPostsSettled(function () {
			if (window.PL_FINISH_MARK) { PL_FINISH_MARK("posts"); }
			postStats("api/exercise-session-end", endParams, function (data) {
				if (window.PL_FINISH_MARK) { PL_FINISH_MARK("end"); }
				if (TrainingSession.latestKey !== sessionKey) {
					// A late reply: another drill has started since. Its screen is not ours to fill.
					if (typeof window.refreshStats === 'function') { window.refreshStats(); }
					return;
				}
				if (window.exerciseSessionEnded) {
					window.exerciseSessionEnded(
						data && data.difficultWords ? data.difficultWords : [],
						data && data.grew ? data.grew : [],
						data && data.wordOutcomes ? data.wordOutcomes : [],
						data && data.difficultMarks ? data.difficultMarks : [],
						data && data.difficultFiled ? data.difficultFiled : [],
						// true only for the session that planted this user's first-ever word —
						// the one-time garden growth-explainer card (main.js).
						!!(data && data.firstGarden),
						// This session's own id, plus whether the owner-only word-decision audit
						// (session-word-audit.md) captured rows for it — false for every non-gated
						// user, so the finish screen's audit pill is absent for everyone else.
						sessionId,
						!!(data && data.auditCaptured));
				} else if (typeof window.refreshStats === 'function') {
					window.refreshStats();
				}
			}, true);
			});
		}
	};

	// Per-word counters rolled up for the finish summary. "Flawless" is the strict read: correct on
	// EVERY presentation with no wrong entries and no timeouts — with repetition 2/3 a word that was
	// missed once doesn't count even if its first pass was clean. Answer speed is the MEDIAN of the
	// per-word averages: a couple of long phrases in the set must not wreck the number.
	function computeSummary() {
		var flawless = 0;
		var perWordAvgMs = [];
		for (var i = 0; i < allDefs.length; i++) {
			var d = allDefs[i];
			if (d.correctCount > 0 && d.correctCount === d.presentedCount
					&& d.wrongEnterCount === 0 && d.timeoutCount === 0) {
				flawless++;
			}
			if (d.correctCount > 0 && d.totalResponseMs > 0) {
				perWordAvgMs.push(d.totalResponseMs / d.correctCount);
			}
		}
		var medianAnswerMs = null;
		if (perWordAvgMs.length > 0) {
			perWordAvgMs.sort(function (a, b) { return a - b; });
			var mid = Math.floor(perWordAvgMs.length / 2);
			medianAnswerMs = perWordAvgMs.length % 2 === 1
				? perWordAvgMs[mid]
				: (perWordAvgMs[mid - 1] + perWordAvgMs[mid]) / 2;
		}
		return {
			wordCount: initialWordCount,
			flawless: flawless,
			sessionMs: sessionStartTs ? (Date.now() - sessionStartTs) : 0,
			medianAnswerMs: medianAnswerMs,
			// The finish screen's bed shows ALL of this session's words, not just the ones that
			// grew — so it needs the word SET, which only the session knows (the server's `grew`
			// payload is by construction the grown subset). Deduped by term: in sense mode several
			// cards share one word, and one word is one plant.
			words: sessionWords(),
			mixed: sessionMixed
		};
	}

	// [{termId, term, reverse}] in session order, one entry per distinct word. `reverse` is the
	// card's own direction (§4) — the finish screen shows it per word only in a Mix session, where
	// it is the one thing the word list cannot otherwise say.
	function sessionWords() {
		var seen = {}, out = [];
		for (var i = 0; i < allDefs.length; i++) {
			var t = allDefs[i].getTerm();
			if (!t || t.id == null || seen[t.id]) { continue; }
			seen[t.id] = 1;
			out.push({ termId: t.id, term: t.term, reverse: !!allDefs[i].reverse });
		}
		return out;
	}

	// Is the drill sitting on a REVEALED answer, waiting for the learner to move on? True from the
	// moment a miss (or a timeout) reveals the word — with its full rich card below it — until the
	// next card is set. main.js reads it so Escape can stand in for Enter there, the way it does on
	// the Learn tab's flipped meet card: what is on screen at that point is a rich view, and Escape
	// is how a rich view is dismissed everywhere else in the app.
	this.isAnswerRevealed = function () {
		return correctAnswerShown;
	};

	this.processUserAnswer = function () {
		if (isSessionStopped) {
			$("#startId").flashClass("errorBackground", 1000, 0);
			return;
		}
		if (!defReturned) {
			var currTs = new Date();
			var enterPressDelay = 1000;//mls
			if (lastWrongAnswerTs && currTs - lastWrongAnswerTs < enterPressDelay) {
				return;
			}
			//The definition wasn't returned - this means the previous answer was wrong -
			//now, just set a new definition
			setNextDefinition();
			return;
		}

		// Collapse runs of whitespace and trim — uniform for every multiword answer (article+noun,
		// phrases, collocations): a double space between words must not fail an otherwise right answer.
		var answer = doc.getAnswerText().replace(/\s+/g, " ").trim();

		if (!answer) {
			revealRequested = true;   // empty Enter = "show me" — logged as REVEAL, not TIMEOUT
			progressBar.moveToEnd();
			return;
		}

		var trainingDef = toShow[currIndex];
		// Strict caps: exact-case forms only (Haus -> "Haus"; Ärger -> "Ärger"/"Aerger"/"Arger");
		// lenient: exact pass first, then case-insensitive. With "Nouns with article" on, the forms
		// arrive article-prefixed ("der Kunde") and the bare noun is rejected — see acceptedForms.
		// Both passes compare on looseKey(), which grades the words and not the punctuation between
		// them — the separators a face prints are optional and interchangeable with a space.
		// Reverse: AnswerForms.matches adds the answer language's own tolerance (typed "sb" ==
		// stored "someone" in English, typed "е" == stored "ё" in Russian) on top of the same
		// loose rules; the loop below stays as its fallback.
		var forms = formsFor(trainingDef);
		var answerKey = looseKey(answer);
		var isCorrect = false;
		if (trainingDef.reverse && window.AnswerForms) {
			isCorrect = AnswerForms.matches(forms, answer, answerLangCode());
		} else if (Util.isVerbFormsCard(trainingDef.card)) {
			// Matched on words, not on the punctuation between them — see verbFormsKey. Against
			// EVERY accepted spelling, not just the card face: the field advertises "ä→ae ö→oe
			// ü→ue ß→ss" on these cards like on any other, and matching forms[0] alone quietly
			// refused the very substitution it was offering ("auftragen, traegt auf, …").
			var answerFormsKey = verbFormsKey(answer);
			for (var vi = 0; vi < forms.length && !isCorrect; vi++) {
				var wantedKey = verbFormsKey(forms[vi]);
				isCorrect = !!wantedKey && answerFormsKey === wantedKey;
			}
		} else {
			for (var fi = 0; fi < forms.length; fi++) {
				if (looseKey(forms[fi]) === answerKey) {
					isCorrect = true;
					break;
				}
			}
			if (!isCorrect && !isCaseStrict(trainingDef)) {//try case insensitive
				var answerKeyUpper = answerKey.toUpperCase();
				for (var fj = 0; fj < forms.length; fj++) {
					if (looseKey(forms[fj]).toUpperCase() === answerKeyUpper) {
						isCorrect = true;
						break;
					}
				}
			}
		}

		if (isCorrect) {
			//answer is correct
			tlPush(trainingDef, 'c', answer.length);
			postPresentationEvent(trainingDef, { correct: true, submitted: answer,
				completionMs: trainingDef.shownAtTs ? Date.now() - trainingDef.shownAtTs : null });
			postAnswerEvent(trainingDef, 'PASS', {answerText: answer});
			trainingDef.correctCount++;
			if (trainingDef.presentedCount === 1 && trainingDef.wrongEnterCountThisPresentation === 0) {
				trainingDef.firstTryCorrect = true;
			}
			if (trainingDef.shownAtTs) {
				trainingDef.totalResponseMs += (Date.now() - trainingDef.shownAtTs);
				trainingDef.shownAtTs = null;
			}
			setAnswer(isCorrect);
			if (trainingDef.isComplete()) {
				var counter = Number(doc.getCounterText());
				if (counter == 0) {
					Util.exception("tsCounterIsZero");
				}
				doc.setCounterText(counter - 1);
				doc.setOverallProgress(initialWordCount ? (initialWordCount - (counter - 1)) / initialWordCount : 0);
			}
			setNextDefinition();
		} else {
			// Re-submitting the exact same wrong answer (Enter again without editing it) is a no-op:
			// don't spawn a fresh encouragement phrase or re-count it as another miss. A genuinely
			// changed attempt (even by one letter) updates lastWrongKey and shows new feedback. Keyed
			// on looseKey so a bare added period isn't treated as a new try either.
			if (answerKey !== trainingDef.lastWrongKey) {
				trainingDef.lastWrongKey = answerKey;
				// Tiered feedback: phrase/color/face follow how close this attempt was (wrong_feedback.js).
				// The same closeness score goes into the answer history row.
				var wfScore = window.WrongFeedback
					? WrongFeedback.score(forms, answer, isCaseStrict(trainingDef)) : null;
				// R39: track this attempt for the reveal-time best-attempt diff (answerWasWrong) --
				// only a genuinely NEW distinct attempt (this whole branch is already gated on that).
				if (wfScore) {
					trainingDef.attemptsThisPresentation.push({text: answer, similarity: wfScore.similarity});
				}
				tlPush(trainingDef, 'c', answer.length);
				postPresentationEvent(trainingDef, { correct: false, submitted: answer,
					similarity: wfScore ? +wfScore.similarity.toFixed(3) : null,
					completionMs: trainingDef.shownAtTs ? Date.now() - trainingDef.shownAtTs : null });
				postAnswerEvent(trainingDef, 'FAIL', wfScore
					? {answerText: answer, tier: wfScore.tier, similarity: wfScore.similarity.toFixed(3)}
					: {answerText: answer});
				doc.setResultText(window.WrongFeedback
					? WrongFeedback.render(forms, answer, isCaseStrict(trainingDef))
					: "<span id='resultLabel' class='tsWrongT1'>" + getRes("tsWrong") + "</span>");
				trainingDef.wrongEnterCount++;
				trainingDef.wrongEnterCountThisPresentation++;
				// Two genuine misses on this word (re-pressing Enter on the same text was skipped by
				// the guard above, so it never gets here) → offer the reveal escape hatch.
				if (trainingDef.wrongEnterCountThisPresentation >= 2) {
					showRevealWord();
				}
			}
		}
	};

	/**
	 * `opts.posted` — the caller already wrote this card's presentation and answer rows and only
	 * wants the reveal. That is the self-check fail path: it knows WHICH failure happened ("I
	 * don't understand" at step 1 vs "I got it wrong" at step 2) where this function would only
	 * ever see a timeout, and posting twice would double-count the miss.
	 */
	var answerWasWrong = function (opts) {
		correctAnswerShown = true;
		lastWrongAnswerTs = new Date();
		hideRevealWord();   // the answer is being revealed now — the button has done its job
		selfCheckBox.hide().empty();   // the buttons have had their answer

		if (trainingDef && opts && opts.posted) {
			// A button "wrong" is a COMMITTED miss, not an expiry — it counts where a typed wrong
			// answer counts, so the finish screen does not report a timeout the learner never had.
			trainingDef.wrongEnterCount++;
			trainingDef.wrongEnterCountThisPresentation++;
			trainingDef.shownAtTs = null;
			revealRequested = false;
		} else if (trainingDef) {
			// Log before the counters/latency reset. Whatever half-typed attempt sits in the
			// field rides along; a plain expiry with an empty field carries no answer text.
			// The M5 row is the attempt the expiry ended: completion_ms stays NULL (censored at
			// the allowance, §4.3) whether the field was blank or half-typed.
			var attemptSoFar = doc.getAnswerText().replace(/\s+/g, " ").trim();
			var expiry = revealRequested ? 'REVEAL' : 'TIMEOUT';
			postPresentationEvent(trainingDef, { correct: false,
				submitted: attemptSoFar || null, completionMs: null });
			postAnswerEvent(trainingDef, expiry,
				attemptSoFar ? {answerText: attemptSoFar} : null);
			trainingDef.timeoutCount++;
			trainingDef.shownAtTs = null;
		}
		revealRequested = false;

		//disable pause button
		$("#startId").prop("disabled", true);

		stopSound();
		// Re-show the cue (sound-only had it hidden) and, in reverse, the drilled sense's own
		// translations under it — the revealed answer must be explicit, not only discoverable
		// inside the rich entry that follows. On a gapped card the frame now renders with the focus
		// word in its blank: the answer is a word IN a sentence, so showing it in place is the
		// teaching moment.
		trainingDef.gapRevealed = true;
		doc.setDefExerciseText(trainingDef.getDefinition()
			+ (trainingDef.reverse ? trainingDef.getRevealAnswerHtml() : ""));

		var defCluster = toShow[currIndex].getDefCluster();
		var termDto = defCluster.term;
		var termStr = termDto.term;
		setAnswer(false);

		// Show WHERE the attempt went wrong: align the BEST pre-reveal attempt of this showing —
		// not, as before, just whatever is left sitting in the answer field — against the nearest
		// accepted spelling and render the per-character diff under the field (R39,
		// garden-srs-redesign-spec.md Sec 3: "the reveal view diffs the BEST match... not the
		// final field state"). Candidates are every changed Enter press this showing already
		// scored (trainingDef.attemptsThisPresentation, via the same WrongFeedback.score the tier
		// feedback used) PLUS the unsubmitted buffer still sitting in the field at the buzzer,
		// scored the same way — deliberately reusing WrongFeedback/AnswerDiff rather than
		// closeness_v2.js, which stays out of the client until P5. Nothing typed at all (no Enter
		// press, empty buffer) keeps today's behavior: no diff.
		// NB: setAnswer(false) above advanced currIndex — the expired card is trainingDef,
		// NOT toShow[currIndex] (which is the next card, or undefined at the cycle's end).
		var strict = isCaseStrict(trainingDef);
		var diffForms = formsFor(trainingDef);
		var finalText = doc.getAnswerText().replace(/\s+/g, " ").trim();
		var attemptCandidates = (trainingDef.attemptsThisPresentation || []).slice();
		if (finalText) {
			var bufferSimilarity = window.WrongFeedback
				? WrongFeedback.score(diffForms, finalText, strict).similarity : 0;
			attemptCandidates.push({text: finalText, similarity: bufferSimilarity});
		}
		if (attemptCandidates.length && window.AnswerDiff) {
			var bestAttempt = attemptCandidates[0];
			for (var ai = 1; ai < attemptCandidates.length; ai++) {
				if (attemptCandidates[ai].similarity > bestAttempt.similarity) {
					bestAttempt = attemptCandidates[ai];
				}
			}
			var closestForm = AnswerDiff.closest(bestAttempt.text, diffForms, strict);
			// The bottom row SAYS which attempt it is. It is the best one of this showing, which is
			// often not the one still sitting in the field — a learner who got closer, then edited
			// their way further off, sees a spelling they no longer have in front of them, and with
			// nothing naming it the panel reads as diffing the wrong text (owner report 2026-08-25).
			// Always shown, single attempt or several: "the best of one" is still true, and a caption
			// that comes and goes is harder to read than one that is simply always there.
			doc.setAnswerDiff(AnswerDiff.renderHtml(closestForm, bestAttempt.text, strict)
				+ "<span class='adCaption'>" + getRes("tsBestAttempt_label") + "</span>");
		}

		// The rich card's own header (headword + IPA + sound + add-to-list) now covers what this
		// panel used to build by hand — just auto-play pronunciation the moment the answer reveals,
		// same as the old sound button did on first render.
		playSound(defCluster);

		// Keep whatever the user had typed (their wrong guess) visible next to the revealed answer —
		// clearing it here was unhelpful. The field is cleared on the next word in setNextDefinition.
		doc.setAnswerPlaceholder(getRes("tsReady_tip"));

		// The answer is now revealed (rich entry below), so the wrong-answer tier phrase has done its job —
		// clear it so it doesn't sit overlaid on top of the revealed content.
		doc.setResultText("");

		// A miss is the best moment to show more than the plain definition — swap in the full rich
		// entry (main.js, a separate closure) if one exists for this word; no-op otherwise, leaving
		// the plain definition already sitting in defExerciseId untouched.
		// A reverse PHRASE card skips this entirely: phrases get no rich lookup (main.js guard),
		// only a prepended headword reveal — and in reverse the phrase is already the cue on
		// screen, so the prepend would just show it twice.
		//
		// On a GAPPED forward card the headword is the focus word, not the sentence. The learner
		// typed one word, so one word is what they got wrong and what belongs in the answer slot;
		// the sentence is already revealed in place by the frame above, with the gap filled, which
		// is the better reveal of the two because it says WHICH word was wanted. Prepending the
		// whole sentence on top of that showed it twice and sized it as if the sentence had been
		// the answer — the same objection the reverse guard above already makes, on the third case
		// it was written before the gap frame existed.
		//
		// A phrase reveal is the bare answer word and nothing else, so it is offered as a click into
		// the real entry — but only when that entry is already STORED, which is what senseIds says
		// (same gate prefetchCardRichEntry uses, same reason: no paid generation, and no sentence
		// sent to the AI as if it were a headword).
		var revealPlan = trainingDef.getGapPlan();
		// The speaker is added by the reveal's own callback, not here: the word branch replaces the
		// whole container when its fetch lands, so anything painted before that is wiped. When no
		// reveal runs at all (the reverse-phrase branch below) the cue on screen is the final one,
		// so it is decorated straight away.
		if (window.showExerciseRichEntry && !(termDto.phrase && trainingDef.reverse)) {
			window.showExerciseRichEntry(termStr, $("#defExerciseId"), termDto.phrase,
				revealPlan ? PhraseGap.expected(revealPlan) : null,
				trainingDef.getSenseIds().length > 0, addRevealSound);
		} else {
			addRevealSound();
		}

		showContinueAction();   // the answer is up — offer the explicit way forward, not just Enter
	};

	var setAnswer = function (answer) {
		if (!defReturned) {
			throw Util.exception("tsDefinitionWasNotSet");
		}
		var td = toShow[currIndex];
		td.addAnswer(answer);
		if (td.isComplete()) {
			td.mastered = true;
		}
		flushCardProgress(td);
		currIndex++;
		defReturned = false;
	};

	function flushCardProgress(td) {
		var senseIds = td.getSenseIds();
		if (!senseIds.length) return;   // legacy sense-less card — drills session-only
		// Sent even before the start's reply: the session key names the session until then (a card
		// answered that fast used to lose its progress row).
		var params = {
			sessionKey: sessionKey,
			senseIds: senseIds,
			timesPresented: td.presentedCount,
			timesCorrect: td.correctCount,
			timesTimedOut: td.timeoutCount,
			wrongEnterAttempts: td.wrongEnterCount,
			soundReplays: td.soundReplayCount,
			firstTryCorrect: td.firstTryCorrect,
			mastered: td.mastered
		};
		if (td.totalResponseMs > 0) params.totalResponseMs = td.totalResponseMs;
		if (sessionId) params.sessionId = sessionId;
		// One request for the whole word — the server upserts every sense's counters in one
		// transaction, so they move together (term mode lockstep).
		inFlightCardPosts.push(postStats("api/exercise-session-sense", params, null));
	}

	var startNewCycle = function () {
		var i = toShow.length;
		while (i--) {
			var trainingDef = toShow[i];
			if (trainingDef.isComplete()) {
				toShow.splice(i, 1);
			}
		}
		currIndex = 0;
		defReturned = false;
		shuffle();
		return toShow.length > 0;
	};

	var shuffle = function () {
		var toShowLastIndex = toShow.length - 1;
		if (toShowLastIndex >= 4) {
			var firstSectionIndex = Math.round(toShowLastIndex * 0.7);
			var arr = randomize(toShow.slice(0, firstSectionIndex + 1));
			toShow = arr.concat(randomize(toShow.slice(firstSectionIndex + 1, toShow.length)));
		}
	};

	var randomize = function (arr) {
		var i = arr.length;
		while (--i) {
			var j = Math.floor(Math.random() * (i + 1));
			var tempI = arr[i];
			arr[i] = arr[j];
			arr[j] = tempI;
		}
		return arr;
	};

	// One drill unit = one sense card {term, senseId, pos, translations, cluster} (see
	// senseCardsOf in main.js). The cluster ref feeds the term-scoped bits — sound, the rich
	// entry on a miss; a plain cluster passed directly (defensive) acts as its own legacy card.
	function TrainingDef(card) {
		this.card = card;
		this.defCluster = card.cluster || card;
		// This card's own direction (main.js stamps it at deck build; a card built by hand or by
		// an older path is forward). Everything direction-dependent in the session reads THIS,
		// never a session-wide flag — that is what makes a Mix session possible.
		this.reverse = !!card.reverse;
		this.answers = [];
		this.presentedCount = 0;
		this.correctCount = 0;
		this.timeoutCount = 0;
		this.wrongEnterCount = 0;
		this.wrongEnterCountThisPresentation = 0;
		// R39 (garden-srs-redesign-spec.md Sec 3): every changed Enter-press attempt of the
		// CURRENT showing, each already scored by WrongFeedback.score for the tier feedback below
		// -- so the reveal-time diff (answerWasWrong) can pick the BEST one instead of always the
		// final field state. {text, similarity} pairs; reset per presentation (setNextDefinition_continue).
		this.attemptsThisPresentation = [];
		this.soundReplayCount = 0;
		this.firstTryCorrect = false;
		this.mastered = false;
		this.totalResponseMs = 0;
		this.shownAtTs = null;
		this.speedSteps = 0;//adaptive-speed steps earned on THIS word (0..SPEED_UP_MAX_STEPS)
		// 0042 per-presentation instrumentation (reset in setNextDefinition_continue per showing)
		this.presentationId = null;
		this.firstKeyMs = null;
		this.keyCount = 0;
		this.backspaceCount = 0;
		this.longestPauseMs = 0;
		this.lastKeyTs = null;
		this.replaysBefore = 0;
		this.autoSoundPlayed = false;
		this.allottedMs = null;
		// M5 capture state (§4.1/§4.4; reset per showing in setNextDefinition_continue)
		this.tlEntries = [];
		this.tlDiverged = false;
		this.tlInBackspaceRun = false;
		this.tlPasted = false;
		this.tlComposed = false;
		this.firstKeyAbsTs = null;
		this.audioEndTs = null;
		// Gap mode: true once the answer has been revealed, so the frame re-renders with the focus
		// word filled in rather than still blank. Reset per showing.
		this.gapRevealed = false;

		// The gap plan for this card, or null when it types in full (design §5 / §7). Forward only,
		// and only for a phrase Term that reads as a SENTENCE and whose focus word resolved.
		// Collocations deliberately keep typing in full whatever the setting says: give away the
		// frame of "sich über etwas beschweren" and you have given away the preposition and the
		// case, which is the only thing it teaches. That is why "Gap long phrases" is worded about
		// LONG phrases and not about phrases in general.
		// Read once per card, at first use — so flipping the setting mid-session applies from the
		// next word rather than re-shaping the card being answered.
		var gapPlan;
		this.getGapPlan = function () {
			if (gapPlan === undefined) {
				var term = this.getTerm();
				// Never in dictation: there the phrase is HEARD and writing it out in full is the
				// whole exercise, so there is no frame to gap and nothing to shorten.
				gapPlan = (isGapPhrases() && !this.reverse && !sessionAudioCue && window.PhraseGap
						&& term && term.phrase && term.focusForm && PhraseGap.isSentence(term.term))
					? PhraseGap.plan(term.term, term.focusForm) : null;
			}
			return gapPlan;
		};

		// "Kunde, der" — the lean article convention: the gender rides along as a gift (the
		// user reads German here, nothing German is typed), matching the tray rows' look.
		//
		// ⭐ Spanish writes it the other way round — "el agua", never "agua, el" — and the two
		// conventions are told apart WITHOUT a language list, because the difference is already in
		// the data: a per-sense FACE (rule F9) is how the word is written, so it leads; a per-TERM
		// `article` column is a gender TAG riding along, so it trails. One fact, one convention,
		// and a language that grows a face gets the right one without editing this function.
		function leanHeadword(termDto, faceArticle) {
			if (faceArticle) {
				return faceArticle + " " + termDto.term;
			}
			return termDto.article ? termDto.term + ", " + termDto.article : termDto.term;
		}

		// Safety net for a card whose sense composes to NOTHING: the plain definition text off
		// the cluster, which every Definition has. A blank cue is unanswerable, so anything is
		// better than nothing — but this is not where phrases are fixed. Phrase Terms carry a
		// real sense of their own now (design §2); what still reaches here is a legacy
		// sense-less word pair, or a sense whose translations are all untaught.
		function clusterDefinitionText(cluster) {
			var defs = (cluster && cluster.definitionList) || [];
			for (var d = 0; d < defs.length; d++) {
				if (defs[d].definition) { return defs[d].definition; }
			}
			return "";
		}

		// The drilled term is polysemous (>=2 senses across its definitions) — the gate for the
		// gloss line appended to the FORWARD cue (a disambiguator for multi-meaning words; a
		// monosemous word's translations already ARE its meaning). Computed once per card.
		var polysemous = null;
		this.isPolysemous = function () {
			if (polysemous === null) {
				var n = 0, defs = this.defCluster.definitionList || [];
				for (var j = 0; j < defs.length; j++) { n += (defs[j].senseGroups || []).length; }
				polysemous = n >= 2;
			}
			return polysemous;
		};

		// The whole cue: the meaning, plus — on a gapped card — the German frame with the focus
		// word blanked under it. Both are needed: with the English alone "The rumour turned out to
		// be false." → type one word is unanswerable, since nothing says which word is wanted.
		//
		// Once the answer is revealed the two of them stop being cue furniture and become the
		// teaching content — the sentence the word lives in and what it means — so they are wrapped
		// and scaled up together (exercise.css). Cue and reveal are the same markup at two sizes,
		// which is why this is a wrapper class and not a second render path.
		this.getDefinition = function () {
			var html = this.getMeaningHtml();
			var plan = this.getGapPlan();
			if (!plan) { return html; }
			var body = html + PhraseGap.frameHtml(plan, this.gapRevealed);
			return this.gapRevealed ? "<div class='_phraseRevealClass'>" + body + "</div>" : body;
		};

		this.getMeaningHtml = function () {
			// A conjugated form, in either direction, is built from the HEAD VERB's senses and a
			// grammar label — never from the cell's own stored translation (see the block above
			// verbTenseHintHtml). Both branches fall through to the generic ones below when the head
			// senses did not arrive, which is exactly today's behaviour.
			var conjSenses = headSensesOf(this.card);
			if (conjSenses && !this.reverse) {
				// Forward: the meaning is the question and the form is the answer, so the senses are
				// the cue and the grammar label says which cell of the paradigm is wanted.
				return "<div class='_posGroupClass _forwardCueClass _conjCueClass'>"
					+ conjKindHtml()
					+ headSensesHtml(conjSenses)
					+ conjSlotHtml(this)
					+ "</div>";
			}
			if (conjSenses && this.reverse) {
				// Reverse: the layout is today's — the form as the cue — and what changes is what is
				// ASKED. The bare cue cannot say which meaning is wanted (that is why reverse has
				// always accepted any sibling sense's translation), and for a paradigm cell it never
				// could: the answer is the verb, and any of ITS meanings is right. Saying so is the
				// difference between a fair card and one that grades a correct answer wrong.
				//
				// The two lines sit under the cue and above the field, so the input stays the last
				// thing on the screen (owner, explicit).
				var rv = "<div class='_posGroupClass _reverseCueClass _conjCueClass'>";
				rv += conjKindHtml();
				var rvHead = leanHeadword(this.getTerm(), this.getFaceArticle());
				rv += "<span class='_senseTransClass' style='--cue-chars: " + rvHead.length + "'>"
					+ escHtml(rvHead) + "</span>";
				rv += verbTenseHintHtml(this.card);
				rv += "<div class='_conjAskClass'>" + escHtml(getRes("tsWhichVerb_question")) + "</div>";
				rv += "<div class='_conjAskSubClass'>" + escHtml(getRes("tsWhichVerb_hint")) + "</div>";
				return rv + "</div>";
			}
			// Reverse cue: the German word itself (lean headword) with a small POS hint —
			// the meaning is what gets typed, so no translations are shown. _reverseCueClass is
			// what scales the headword up (design §3): one lean word in a pane sized for multi-line
			// glosses, so it takes the room. On the CUE only — the revealed meaning below it
			// (getRevealAnswerHtml) builds its own plain _posGroupClass and stays normal size.
			if (this.reverse) {
				var r = "<div class='_posGroupClass _reverseCueClass'>";
				if (this.card.pos) {
					r += PosLabel.html(this.card.pos) + "<br>";
				}
				// --cue-chars is how the CSS sizes the word to the room it has: the type scales up
				// to 2.6rem for an ordinary headword and steps down just enough for a 33-character
				// compound to still land on one line (see exercise.css, design §3).
				var head = leanHeadword(this.getTerm(), this.getFaceArticle());
				r += "<span class='_senseTransClass' style='--cue-chars: " + head.length + "'>"
					+ head + "</span>";
				r += verbTenseHintHtml(this.card);
				return r + "</div>";
			}
			// Term mode: the whole word is one drill unit, so the cue is its COMPOSED gloss (main
			// sense's top translations + one per remaining sense) — the same one line the Learn rungs
			// use. Type the word to answer. Forward only (reverse is handled above).
			if (this.card.termCard) {
				// _forwardCueClass + --cue-chars: the same length-aware scaling the reverse cue
				// uses (exercise.css, design §3). A one-word meaning ("light") gets the room the
				// pane has; a long gloss sizes itself back down to today's face and wraps as before.
				var tg = "<div class='_posGroupClass _forwardCueClass'>";
				if (this.card.pos) { tg += PosLabel.html(this.card.pos) + "<br>"; }
				var gloss = this.card.gloss || clusterDefinitionText(this.defCluster);
				tg += "<span class='_senseTransClass' style='--cue-chars: " + gloss.length + "'>"
					+ gloss + "</span>";
				// A principal-parts card carries the same two extra lines here as it does on a sense
				// card — term mode is the DEFAULT study unit, so this is the branch it actually
				// renders through. Its composed gloss above is just the translations, so the sense's
				// own gloss has to be read off the cluster; without it the three near-synonyms behind
				// one Russian meaning (say/tell, do/make) lose what tells them apart.
				if (Util.isVerbFormsCard(this.card)) {
					var sg = firstSenseGloss(this.defCluster);
					if (sg) { tg += "<span class='_senseGlossClass'>" + sg + "</span>"; }
					tg += verbFormsHintHtml(this.getTerm());
				}
				tg += verbTenseHintHtml(this.card);
				return tg + "</div>";
			}
			// A sense card's cue is ITS OWN meaning only — the point of the sense flip: no
			// sibling translations jammed together ("to resign · to lie down · to record …").
			// For a polysemous term the sense's gloss rides under the translations to disambiguate
			// which meaning is being asked (forward only — this branch is unreachable under reverse,
			// where an English gloss would leak the typed answer).
			if (this.card.senseId != null) {
				var s = "<div class='_posGroupClass _forwardCueClass'>";
				if (this.card.pos) {
					s += PosLabel.html(this.card.pos) + "<br>";
				}
				var composed = (this.card.translations || []).join(' · ')
					|| clusterDefinitionText(this.defCluster);
				s += "<span class='_senseTransClass' style='--cue-chars: " + composed.length + "'>"
					+ composed + "</span>";
				// A principal-parts card ALWAYS shows its gloss, polysemous or not. The card it was
				// built from carries exactly one sense, so the usual test would hide it — and this is
				// the card that needs it most: three near-synonymous English verbs sit behind one
				// Russian gloss (say/tell, do/make, lay/put), and the sibling translations plus the
				// gloss are the whole of what tells them apart.
				var isForms = Util.isVerbFormsCard(this.card);
				if (this.card.gloss && (isForms || this.isPolysemous())) {
					s += "<span class='_senseGlossClass'>" + this.card.gloss + "</span>";
				}
				if (isForms) {
					s += verbFormsHintHtml(this.getTerm());
				}
				s += verbTenseHintHtml(this.card);
				return s + "</div>";
			}
			// Legacy sense-less card: the whole cluster, exactly as the term-grained drill showed it.
			var termDefinitions = this.defCluster.definitionList;
			var defStr = "";
			for (var j = 0; j < termDefinitions.length; j++) {
				var def = termDefinitions[j];
				if (j > 0) {
					defStr += "<br>";
				}
				if (def.posGroups && def.posGroups.length > 0) {
					for (var k = 0; k < def.posGroups.length; k++) {
						var group = def.posGroups[k];
						defStr += "<div class='_posGroupClass'>";
						defStr += PosLabel.html(group.pos) + "<br>";
						defStr += "<span class='_senseTransClass'>" + group.translations.join(' · ') + "</span>";
						defStr += "</div>";
					}
				} else {
					if (def.partOfSpeech) {
						defStr += PosLabel.html(def.partOfSpeech) + "<br>";
					}
					defStr += "&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;" + def.definition;
				}
			}
			return defStr;
		};

		// The reverse reveal: the meaning(s) to teach under the cue, shown the same way the forward
		// cue shows them. Term mode reveals the composed gloss (the whole word); a sense card its own
		// translations. A card from a pair with no rich senses has neither — there the card IS the
		// whole word, so its definition text is the meaning being taught, exactly as the forward
		// cue shows it for the same card.
		this.getRevealAnswerHtml = function () {
			// A conjugated card reveals what it accepted: the head verb's senses, the same list the
			// forward direction cues with. Its own stored translation is not shown here for the same
			// reason it is not shown anywhere else — it was invented per cell.
			var conjSenses = headSensesOf(this.card);
			if (conjSenses) {
				return "<div class='_posGroupClass'>" + headSensesHtml(conjSenses) + "</div>";
			}
			var body = this.card.termCard ? (this.card.gloss || "") : (this.card.translations || []).join(" · ");
			if (!body) { body = definitionTextOf(this.defCluster); }
			return "<div class='_posGroupClass'><span class='_senseTransClass'>" + body + "</span></div>";
		};

		// Every translation the WHOLE term carries (all senses, display order) — the reverse
		// accepted-answer pool, computed once per card. The extraction itself lives in
		// AnswerForms.translationsOf: it is the same question the deck build asks when it decides
		// whether a card CAN be drilled in reverse, and the two must never drift apart.
		var termTranslations = null;
		this.getTermTranslations = function () {
			if (!termTranslations) {
				// A conjugated cell answers with its HEAD VERB's translations, not its own. Same
				// argument the generic line below is built on — "the bare-word cue can't say which
				// meaning is asked, so a sibling sense's translation is a right answer too" — pointed
				// one level up, at the term the cue is actually a form of. The cue now says so out
				// loud («любое из значений глагола»), so this is what makes that promise true.
				var conjSenses = headSensesOf(this.card);
				if (conjSenses) {
					termTranslations = acceptedHeadTranslations(conjSenses);
				} else {
					termTranslations = window.AnswerForms
						? AnswerForms.translationsOf(this.defCluster) : [];
					// A thin `word_form` row (`drew`, `Häuser`, `größer`) takes the head word's
					// senses as WELL — a union, and the difference from the conjugated branch above
					// is the whole point (§6a, ruling 1). A cell's invented gloss was unsound and is
					// disowned; a form row's line is CORRECT and must stay accepted — «рисовал, -а,
					// -о, -и» is the right answer for `drew` and replacing the pool would grade it
					// wrong. It is merely INCOMPLETE: `draw` also means "to attract", so «привлёк»
					// is equally right and today's pool marks it wrong. Absent field ⇒ untouched.
					termTranslations = unionTranslations(termTranslations,
						acceptedHeadTranslations(this.card.formHeadSenses || []));
				}
			}
			return termTranslations;
		};

		// Plain-text version of the visual cue, for the answer-event history's cue_shown column.
		// A gapped card's frame is part of what has to be read, so it counts towards cue length —
		// which is what the L* floor and the label derivation's censored-short test are sized on.
		this.getCueText = function () {
			var plan = this.getGapPlan();
			if (plan) { return this.getMeaningCueText() + " — " + PhraseGap.plainText(plan); }
			return this.getMeaningCueText();
		};

		this.getMeaningCueText = function () {
			// The history's cue_shown must record what was on the screen, so it follows the same
			// branch getMeaningHtml took: a conjugated card's cue is the head verb's senses plus the
			// slot, never the cell's own translation.
			var conjSenses = headSensesOf(this.card);
			if (conjSenses && !this.reverse) {
				var shown = conjSenses.slice(0, CONJ_SENSES_SHOWN)
					.map(function (s) { return (s || []).join(" · "); }).join(" | ");
				var slot = [Util.conjTenseLine(this.card.verbTense),
					Util.conjPersonLine(this.getTerm() && this.getTerm().term)]
					.filter(function (p) { return !!p; }).join(" · ");
				return slot ? shown + " — " + slot : shown;
			}
			if (this.reverse) {
				return (this.card.pos ? this.card.pos + ": " : "")
					+ leanHeadword(this.getTerm(), this.getFaceArticle());
			}
			if (this.card.termCard) {
				return (this.card.pos ? this.card.pos + ": " : "") + (this.card.gloss || "");
			}
			if (this.card.senseId != null) {
				var cue = (this.card.pos ? this.card.pos + ": " : "")
					+ (this.card.translations || []).join(" · ");
				if (this.isPolysemous() && this.card.gloss) { cue += " — " + this.card.gloss; }
				return cue;
			}
			var parts = [];
			var defs = this.defCluster.definitionList || [];
			for (var j = 0; j < defs.length; j++) {
				var groups = defs[j].posGroups || [];
				for (var k = 0; k < groups.length; k++) {
					parts.push((groups[k].pos ? groups[k].pos + ": " : "")
						+ groups[k].translations.join(" · "));
				}
				if (!groups.length && defs[j].definition) {
					parts.push(defs[j].definition);
				}
			}
			return parts.join(" | ");
		};

		this.getDefCluster = function () {
			return this.defCluster;
		};

		this.getSenseId = function () {
			return this.card.senseId != null ? this.card.senseId : null;
		};

		// Every sense this drill unit stands for: its one sense (sense mode) or ALL the word's senses
		// (term mode) — the stat/event posts write to each, so a term's progress advances in lockstep.
		this.getSenseIds = function () {
			if (this.card.senseIds && this.card.senseIds.length) { return this.card.senseIds; }
			return this.card.senseId != null ? [this.card.senseId] : [];
		};

		this.getTerm = function () {
			return this.card.term || this.defCluster.term;
		};

		// Rule F9's card-face article for THIS card's sense, or "" — resolved once per card and
		// cached, because it is read by the cue, the accepted set and the timer on every showing.
		this.getFaceArticle = function () {
			if (this._faceArticle === undefined) {
				this._faceArticle = senseFaceArticle(this.defCluster, this.card);
			}
			return this._faceArticle;
		};

		this.addAnswer = function (answer) {
			if (typeof answer !== "boolean") {
				throw Util.exception("addAnswer: param is not of type boolean.");
			}
			this.answers.push(answer);
			//adaptive speed: a correct answer earns a speed-up step for this word's next
			//appearance, a miss (wrong or timed out) gives one back — never below normal
			if (answer) {
				this.speedSteps = Math.min(this.speedSteps + 1, SPEED_UP_MAX_STEPS);
			} else if (this.speedSteps > 0) {
				this.speedSteps--;
			}
		};

		// Repetition setting (1..3) — the strictness slider's showings fold-in, or 1 while the
		// onboarding tour's pin is live (getRepetition) — drives when a word retires:
		//   clean run (no reveal yet): `repetition` correct showings — 1, 2 or 3.
		//   after any reveal: the last repetition + 2 showings must all be correct — 3, 4 or 5 in a row.
		// Repetition 1 is exactly the historical rule (first-try correct, or 3 correct in a row).
		// A "reveal" ends a showing (countdown out, empty Enter, or the reveal button after two
		// misses); wrong Enters inside the countdown do not — a showing answered before the buzzer
		// is correct however many tries it took. 2026-09-01 (owner ruling,
		// docs/project/verdict-follows-drill-plan.md): a clean run at repetition 3 used to retire
		// after 2, same as repetition 2 — the July setting only told 2 from 3 after a miss — while
		// the slider's notches 8–10 caption "three times running" and the server verdict planned 3,
		// so every clean word there sealed VOID. The server replica of this rule is
		// SrsLabelRules.evaluateCriterion — change both or neither.
		this.isComplete = function isComplete() {
			var answers = this.answers;
			var length = answers.length;
			if (length === 0) {
				return false;
			}
			var repetition = getRepetition();
			if (answers.indexOf(false) === -1) {//clean run so far
				return length >= repetition;
			}
			var needed = repetition + 2;
			if (length < needed) {
				return false;
			}
			for (var i = length - needed; i < length; i++) {
				if (!answers[i]) {
					return false;
				}
			}
			return true;
		};
	}

	function ProgressBar(doOnExpiry) {
		var intervalId;
		var currPercent = 0;
		var bar = $("#progressBarId");
		var _trainingDef;

		this.start = function (trainingDef) {
			_trainingDef = trainingDef;
			this.stop();//make sure the previous timer is stopped
			this.clear();
			this.setToZero();
			this.resetTimePercentage();
			// 0042: stash this presentation's timer allowance for the answer history — allotted_ms
			// de-censors latency (a TIMEOUT's latency ≈ allotted) and folds speed setting,
			// adaptive-speed steps and miss extension into one number.
			if (trainingDef) {
				trainingDef.allottedMs = Math.round(getAnswerTime() * 1000);
			}
			this.resume();
		};

		this.stop = function () {
			this.pause();
			this.resetTimePercentage();
		};

		this.pause = function () {
			if (intervalId) {
				window.clearInterval(intervalId);
			}
		};

		this.resume = function () {
			var step = 100 / (getAnswerTime() * 50);
			var timer = this;
			intervalId = setInterval(function () {
				currPercent += step;
				if (currPercent > 100) {
					currPercent = 100;
				}
				bar.width(currPercent + "%");
				var color = getColor(currPercent);
				bar.css('background-color', color);
				if (currPercent == 100) {
					timer.stop();
					doOnExpiry();
				}
			}, 20);
		};

		this.clear = function () {
			bar.width("100%");
			bar.css('background-color', 'transparent');
		};

		this.setToZero = function () {
			bar.width(0);
		};

		this.resetTimePercentage = function () {
			currPercent = 0;
		};

		this.hide = function () {
			bar.hide();
		};

		this.moveToEnd = function() {
			bar.width("100%");
			var color = getColor(100);
			bar.css('background-color', color);
			this.stop();
			doOnExpiry();
		};

		var secPerLetterArr = [5, 4, 3, 2, 1.5, 1.2, 1, 0.7, 0.4, 0.2];

		var getAnswerTime = function () {
			var speed = parseInt($("#speedValId").text(), 10);
			if (!speed) {
				speed = 4;
			}
			var secPerLetter = secPerLetterArr[speed - 1];
			// A self-check card's clock pays for something else entirely (§6.1): nothing is
			// typed, so the per-letter budget has no meaning. Reading allowance over the cue plus
			// a fixed recall time on the speed dial, and it covers step 1 only — the reveal stops
			// it. Returned before the typing formula because none of that formula applies.
			if (_trainingDef.selfCheck) {
				return SelfCheck.answerTime((_trainingDef.getCueText() || "").length, secPerLetter);
			}
			// T-10 (docs/project/tablet-support-plan.md §4): the per-letter typing allowance on glass
			// (R7). PL.drillSecPerLetter is unset on a desktop; js/touch/drill.js sets it. Below the
			// self-check return on purpose: nothing is typed there.
			if (window.PL && PL.drillSecPerLetter) {
				secPerLetter = PL.drillSecPerLetter(secPerLetter, currentDirection(_trainingDef));
			}
			var answerArr = _trainingDef.answers;
			var gapPlan = _trainingDef.getGapPlan();
			var numOfLetters;
			if (_trainingDef.reverse) {
				// Reverse: what's typed is an English translation — time the SHORTEST accepted
				// form (the least typing that can be right), not the German word's length.
				var forms = formsFor(_trainingDef);
				numOfLetters = 0;
				for (var fi = 0; fi < forms.length; fi++) {
					if (!numOfLetters || forms[fi].length < numOfLetters) {
						numOfLetters = forms[fi].length;
					}
				}
				if (!numOfLetters) {
					numOfLetters = _trainingDef.getTerm().termSearch.length;
				}
			} else if (gapPlan) {
				// A gapped card is not a whole-phrase typing task: the clock pays for what is
				// actually TYPED — the focus word — instead of the forty characters on screen.
				numOfLetters = PhraseGap.expected(gapPlan).length;
			} else {
				numOfLetters = _trainingDef.getTerm().termSearch.length;
				// With "Nouns with article" on, the answer must be typed article-prefixed ("der
				// Kunde"), so give the timer credit for those extra keystrokes: article + space.
				var article = articleFor(_trainingDef.getTerm(), _trainingDef.getFaceArticle());
				if (article) {
					numOfLetters += article.length + 1;
				}
			}
			var normalTime = 1 + secPerLetter * numOfLetters;
			// …and the other half of that split: the frame still has to be READ before the one word
			// can be found. Reading is length-dependent where recall and typing are not, so it is a
			// flat per-character allowance on the frame, NOT scaled by the speed setting — speeding
			// the drill up should shorten the thinking, not make a nine-word sentence unreadable.
			// Added before the adaptive/miss math so those scale the whole allowance.
			if (gapPlan) {
				normalTime += GAP_READ_SEC_PER_CHAR * gapPlan.text.length;
			}
			if (isAdaptiveSpeed() && _trainingDef.speedSteps > 0) {
				//adaptive speed: each step earned on this word shortens its timer by
				//SPEED_UP_STEP_PERCENT multiplicatively (10 -> 9 -> 8.1 -> 7.29 s...); the
				//integer step counter guarantees the way back up retraces the same values
				normalTime *= Math.pow(1 - SPEED_UP_STEP_PERCENT / 100, _trainingDef.speedSteps);
			}
			var numOfLastWrongAnswers = 0;
			for (var i = answerArr.length - 1; i >= 0; i--) {
				if (!answerArr[i]) {
					numOfLastWrongAnswers++;
				} else {
					break;
				}
			}
			var increaseTime = normalTime * (MISS_EXTENSION_PERCENT / 100) * numOfLastWrongAnswers;
			var time = normalTime + increaseTime;
			var maxTime = normalTime * MISS_EXTENSION_MAX_FACTOR;
			time = maxTime < time ? maxTime : time;
			// §4.3 floor: whatever speed setting, adaptive tightening and mercy math produced,
			// the allowance in force never lands below L* for this showing. Same target string
			// (requiredTarget = first accepted form) and same cueLen expression the presentation
			// event posts, so the label derivation's censored-short test sees allotted ≥ L*.
			var floorTarget = (formsFor(_trainingDef) || [])[0] || null;
			var floorCue = sessionAudioCue ? null : _trainingDef.getCueText();
			var floorMs = plLStarFloorMs(floorTarget, floorCue ? floorCue.length : 0,
				plDeviceClass, currentDirection(_trainingDef));
			if (floorMs > 0 && time * 1000 < floorMs) {
				time = floorMs / 1000;
			}
//			log("time=" + time + " (let=" + numOfLetters + ", sec/let=" + secPerLetter + ", norm=" + normalTime
//			+ ", incr=" + increaseTime + ", wrong=" + numOfLastWrongAnswers + ")");
			return  time;
		};

		// Timer fill: interpolates the two --pl-timer-* tokens (calm green → calm red)
		// by elapsed percent. Repainting the ramp = retuning the tokens (Phase 3d
		// batch 6; the endpoints used to be hardcoded pure #0f0→#f00 channel math).
		var getColor = function (percent) {
			var from = plTokenColor("--pl-timer-fresh");
			var to = plTokenColor("--pl-timer-out");
			var f = percent / 100;
			var mix = function (i) { return Math.round(from[i] + (to[i] - from[i]) * f); };
			// Near-opaque: at .4 the fill was so see-through its rounded leading edge vanished
			// into the track and read as a square edge. .85 keeps the colour vivid AND
			// lets the border-radius actually show.
			return "rgb(" + mix(0) + ", " + mix(1) + ", " + mix(2) + ", 0.85)";
		};
	}

	function Doc() {
		// Every cue this session renders reaches the screen through here, so it is the one seam
		// that tooltips the cue's pos labels — the lean rows have their own in buildEntryElement.
		// (main.js declares an identically named method on ITS Doc, but nothing calls that one.)
		this.setDefExerciseText = function (text) {
			PosLabel.wire(defExerciseField.html(text));
		};

		this.getLookupText = function () {
			return lookupField.val();
		};

		this.setLookupText = function (text) {
			lookupField.val(text);
		};

		this.getAnswerText = function () {
			var typed = answerField.val();
			// An empty field stays empty even with a pronoun on screen: bare Enter means "show me"
			// (processUserAnswer reads it as a reveal), and handing back the lone pronoun would be
			// scored as a wrong answer instead.
			if (!answerPrefix || !$.trim(typed)) {
				return typed;
			}
			return answerPrefix + " " + typed;
		};

		this.setAnswerText = function (text) {
			answerField.val(text);
		};

		this.setResultText = function (text) {
			$("#exerciseResultId").html(text);
		};

		this.setAnswerDiff = function (html) {
			$("#answerDiffId").html(html);
		};

		this.setTipText = function (text) {
			tipBox.text(getRes(text));
		};

		// Plain native placeholder behavior for both hints (the "type the translation" prompt and
		// the "press Enter for the next word" one after a miss): visible whenever the field is
		// empty, regardless of focus — gone once the user actually types something.
		this.setAnswerPlaceholder = function (text) {
			// Leading em-space so the grey hint doesn't hug the blinking caret (the caret sits at the
			// field's left padding; this pushes only the placeholder glyphs, not typed text, rightward).
			answerField.attr('placeholder', text ? " " + text : '');
		};

		this.setCounterText = function (count) {
			$("#counterId").text(count);
		};

		this.getCounterText = function () {
			return $("#counterId").text();
		};

		// Overall set progress, 0..1 — width AND colour of the top (never-resetting) bar. The fill
		// deepens from light lavender (barely started) to intense violet (complete), interpolating
		// each RGB channel by the fraction — the violet analogue of the timer bar's green→red.
		this.setOverallProgress = function (fraction) {
			var f = Math.max(0, Math.min(1, fraction || 0));
			$("#overallProgressBarId")
				.width(f * 100 + "%")
				.css("background-color", overallProgressColor(f));
		};

		// Endpoints live in the design tokens (Phase 3d batch 6): faint = the bar's 0%
		// resting colour, vivid = the "progress energy" tier — so D14's re-hue of
		// --pl-accent-vivid repaints this ramp with no JS change.
		var overallProgressColor = function (f) {
			var from = plTokenColor("--pl-accent-faint");
			var to = plTokenColor("--pl-accent-vivid");
			var mix = function (i) { return Math.round(from[i] + (to[i] - from[i]) * f); };
			return "rgb(" + mix(0) + ", " + mix(1) + ", " + mix(2) + ")";
		};
	}
}
